import { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { abortController } from '../../utils/abortController';
import useUser from '../../hooks/useUser';
import useSessionExpire from '../../hooks/useSessionExpire';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import FileUploader from '../../components/FileUploader';
import VirtualizedAutocomplete from '../../components/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import InfoIcon from '@mui/icons-material/Info';
import Typography from '@mui/material/Typography';
import ReportDownloader from '../../components/ReportDownloader';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Modal from 'react-bootstrap/Modal';
import ButtonGroup from '@mui/material/ButtonGroup';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';
import dayjs from 'dayjs';
import { isFieldLocked } from '../../utils/fieldUtils';
import { InputAdornment } from '@mui/material';
import DocumentReadyNotice from '../../components/DocumentReadyNotice';
import CombinedMissingFieldsModal from '../../components/CombinedMissingFieldsModal';
import useTab from '../../hooks/useTab';

const Documentation = forwardRef(({ isWritable = false, isDocumentWritable = false, areDocumentsLoading = false, isVantiraDeal = false, isListDataLoading = false, areFilesLoading = false, isExistingDocumentation = false, isFoodsOrPolymersDeal = false, isSBLCDeal = false, isUpdated = false, selectedBusinessUnit = null, selectedPFI = null, selectedTranche = null, isLinkedThroughRoute = false, linkedPFI = null, linkedTranche = null, listData = {}, banks = [], documentPermissions = {}, documentRequestPermissions = {}, formData = {}, lockedFields = [], files = [], uploadedFiles = [], requestedFiles = [], toggleDocumentList = false, toggleFileList = false, expandedFormSections = [], commmercialInvoiceNo = null, setCommmercialInvoiceNo = () => { }, setFormData = () => { }, setFiles = () => { }, setUploadedFiles = () => { }, setRequestedFiles = () => { }, setToggleDocumentList = () => { }, setToggleFileList = () => { }, setExpandedFormSections = () => { }, setIsDocumentToBeGenerated = () => { }, setShowConfirmationDialog = () => { }, setIsLoading = () => { }, setShowAlert = () => { }, setAlertMessage = () => { }, setAlertType = () => { }, logOut = () => { } }, ref) => {

  const {
    selectedDeal = false,
  } = useTab();

  const { pathname } = useLocation();

  /* AUTHENTICATION VARIABLES */
  const currentUser = useUser();

  /* SESSION EXPIRY VARIABLES */
  const triggerSessionExpire = useSessionExpire();

  /* LIST VARIABLES */
  const {
    buyingLegFileTypes = [],
    sellingLegFileTypes = [],
  } = listData;
  const discrepancyAcceptanceSwifts = useMemo(() => ["Yes", "No"], []);
  const transactionTypes = useMemo(() => ["Addition", "Subtraction"], []);
  const buyerDocumentStatus = useMemo(() => ["Received - Clean", "Received - Discrepant"], []);
  const sellerDocumentStatus = useMemo(() => ["Dispatched - Clean", "Dispatched - Discrepant"], []);

  const isCommercialInvoiceEditable = isVantiraDeal && isLinkedThroughRoute;

  /* FORM VARIABLES */
  const formRef = useRef(null);

  /* SELECTED LIST VARIABLES */
  const [selectedCOO, setSelectedCOO] = useState(null);
  const [selectedDocumentRevisionNumber, setSelectedDocumentRevisionNumber] = useState(null);

  /* DOCUMENT VARIABLES */
  const coos = useSelector(state => state.dealUploadedDocuments.certificatesOfOrigin) || [];
  const documentRevisionNumbers = useSelector(state => state.dealUploadedDocuments.documentRevisionNumbers) || [];
  const documentGenerationData = useSelector(state => state.dealUploadedDocuments.documentGenerationData) || {};
  const [draftDocuments, setDraftDocuments] = useState([]);
  const [approvedDocuments, setApprovedDocuments] = useState([]);
  const [documentType, setDocumentType] = useState(null);
  const [addTermsAndConditionsPage, setAddTermsAndConditionsPage] = useState(false);

  // LINKED TRANCHE DOCUMENT VARIABLES
  const [selectedLinkedCOO, setSelectedLinkedCOO] = useState(null);
  const [linkedTrancheCoos, setLinkedTrancheCoos] = useState([]);
  const [isLoadingLinkedCoos, setIsLoadingLinkedCoos] = useState(false);

  /* DOCUMENT BUTTON VARIABLES */
  const [isPFIDocumentGenerating, setIsPFIDocumentGenerating] = useState(false);
  const [isSIDocumentGenerating, setIsSIDocumentGenerating] = useState(false);
  const [isCIDocumentGenerating, setIsCIDocumentGenerating] = useState(false);
  const [isSADocumentGenerating, setIsSADocumentGenerating] = useState(false);
  const [isPLDocumentGenerating, setIsPLDocumentGenerating] = useState(false);
  const [isBEDocumentGenerating, setIsBEDocumentGenerating] = useState(false);
  const [isCOODocumentGenerating, setIsCOODocumentGenerating] = useState(false);
  const pfiDocumentDownloadButtonRef = useRef(null);
  const siDocumentDownloadButtonRef = useRef(null);
  const ciDocumentDownloadButtonRef = useRef(null);
  const saDocumentDownloadButtonRef = useRef(null);
  const plDocumentDownloadButtonRef = useRef(null);
  const beDocumentDownloadButtonRef = useRef(null);
  const cooDocumentDownloadButtonRef = useRef(null);

  // Add linked document refs
  const linkedPfiDocumentDownloadButtonRef = useRef(null);
  const linkedSiDocumentDownloadButtonRef = useRef(null);
  const linkedCiDocumentDownloadButtonRef = useRef(null);
  const linkedSaDocumentDownloadButtonRef = useRef(null);
  const linkedPlDocumentDownloadButtonRef = useRef(null);
  const linkedBeDocumentDownloadButtonRef = useRef(null);
  const linkedCooDocumentDownloadButtonRef = useRef(null);

  const [selectedDocumentGenerationButtonRef, setSelectedDocumentGenerationButtonRef] = useState(null);
  const [selectedLinkedDocumentGenerationButtonRef, setSelectedLinkedDocumentGenerationButtonRef] = useState(null);

  const [showMissingFieldsModal, setShowMissingFieldsModal] = useState(false);

  // 2. Update combinedMissingFields initial state (remove 'show')
  const [combinedMissingFields, setCombinedMissingFields] = useState({
    vantira: [],
    fze: [],
    pendingCalls: 2  // or however many calls you expect
  });


  // Determine entity types
  const mainEntityType = selectedDeal?.is_vantira_deal ? 'vantira' : 'fze';
  const linkedEntityType = mainEntityType === 'vantira' ? 'fze' : 'vantira';

  // Handler for when missing fields are detected
  const handleMissingFieldsDetected = (entityType, fields) => {

    setCombinedMissingFields(prev => ({
      ...prev,
      [entityType]: fields || [],
      pendingCalls: prev.pendingCalls - 1
    }));
  };

  // 4. Add useEffect to trigger modal
  useEffect(() => {

    // Only check when all calls are complete
    if (combinedMissingFields.vantira.length > 0 || combinedMissingFields.fze.length > 0) {
      const hasVantiraFields = combinedMissingFields.vantira.length > 0;
      const hasFzeFields = combinedMissingFields.fze.length > 0;
      const shouldShowModal = hasVantiraFields || hasFzeFields;

      console.log("All API calls complete:", {
        vantiraCount: combinedMissingFields.vantira.length,
        fzeCount: combinedMissingFields.fze.length,
        shouldShowModal
      });

      if (shouldShowModal) {
        setShowMissingFieldsModal(true);
      }
    }
  }, [combinedMissingFields.vantira.length, combinedMissingFields.fze.length]); // Trigger when counts change from 0 to >0

  const handleCloseCombinedModal = () => {
    setShowMissingFieldsModal(false);
    // Reset the fields if needed
    setCombinedMissingFields({
      vantira: [],
      fze: [],
      pendingCalls: 2 // Reset to initial pending calls
    });
  };


  /* INPUT REFERENCE VARIABLES */
  const freightInputRef = useRef(null);
  const fobValueInputRef = useRef(null);

  /* TAB VARIABLES */
  const [selectedTab, setSelectedTab] = useState(0);

  /* MODAL VARIABLES */
  const [showDocumentModal, setShowDocumentModal] = useState(false);

  useImperativeHandle(ref, () => (
    {
      isValid,
      handleCloseModal: handleCloseModal
    }));


  const convertToDayJsObject = (date) => {
    const convertedDate = date ? dayjs(date, "MM/DD/YYYY") : null;
    return convertedDate;
  }

  const calculateMaturityDate = (counterPartyType) => {
    const shipmentDate = formData?.Shipment_Date ? dayjs(formData.Shipment_Date) : null;
    const tenorDays = formData?.[counterPartyType === "Customer" ? "Selling_Tenor_Days" : "Buying_Tenor_Days"] || 0;
    const maturityDate = shipmentDate ? shipmentDate.add(tenorDays, 'day') : null;

    return maturityDate;
  }

  const isValid = () => {
    return formRef.current?.reportValidity();
  }

  const handleSectionAccordionChange = (section) => (_, isExpanded) => {
    setExpandedFormSections(isExpanded ? [...expandedFormSections, section] : expandedFormSections.filter(id => id !== section));
  }

  const handleChange = (event, field, value, isDate = false) => {
    let newValue = value;
    if (isDate && newValue) {
      newValue = dayjs(newValue).format("MM/DD/YYYY");
    }
    if (["Advance_Amount", "Total_Amount"].includes(field)) {
      // Remove any non-numeric characters except the decimal point
      newValue = value.replace(/[^0-9.]/g, "");
      // Ensure the value has at most 3 decimal places
      if (newValue.includes(".")) {
        const [integerPart, decimalPart] = newValue.split(".");
        newValue = `${integerPart}.${decimalPart.substring(0, 3)}`;
      }
    } else if (["Freight", "FOB_Value", "Discount"].includes(field)) {
      // Remove any non-numeric characters except the decimal point
      newValue = value.replace(/[^0-9.]/g, "");
      // Ensure the value has at most 2 decimal places
      if (newValue.includes(".")) {
        const [integerPart, decimalPart] = newValue.split(".");
        newValue = `${integerPart}.${decimalPart.substring(0, 3)}`;
      }
    }
    if (field === "Freight" || field === "FOB_Value") {
      freightInputRef?.current?.setCustomValidity("");
      fobValueInputRef?.current?.setCustomValidity("");
      // Use parseFloat to handle decimals
      let freight = parseFloat(formData["Freight"] || 0);
      let fobValue = parseFloat(formData["FOB_Value"] || 0);
      if (field === "Freight") {
        freight = parseFloat(newValue || 0);
      } else if (field === "FOB_Value") {
        fobValue = parseFloat(newValue || 0);
      }
      // Calculate total with decimals and round to 3 places for Total_Amount comparison
      const calculatedTotalAmount = parseFloat((freight + fobValue).toFixed(3));
      const actualTotalAmount = parseFloat(formData["Total_Amount"] || 0);
      if (calculatedTotalAmount > actualTotalAmount) {
        event?.target?.setCustomValidity("The calculated total is more than the actual total amount.");
      } else if (calculatedTotalAmount < actualTotalAmount) {
        event?.target?.setCustomValidity("The calculated total is less than the actual total amount.");
      } else {
        event?.target?.setCustomValidity("");
      }
    }
    setFormData((previousFormData) => ({
      ...previousFormData,
      [field]: typeof newValue === "string" ? newValue.trim() === "" ? null : newValue : newValue ? newValue : null
    }));
  };

  const handleTabChange = (tab) => {
    setSelectedDocumentRevisionNumber(null);
    setSelectedCOO(null);
    setSelectedTab(tab);
  }

  const handleViewDocument = async () => {
    setIsLoading(true);
    setShowAlert(false);
    setAlertMessage("");
    setAlertType("");

    await axios({
      method: "post",
      url: "/api/ViewDocument",
      data: {
        pathname: pathname,
        Deal_PFI_Id: selectedPFI,
        Tranche_Id: selectedTranche?.id,
        Document_Type: documentType,
        COO_ID: selectedCOO?.id,
        Revision_Number: selectedDocumentRevisionNumber?.value === "Default" ? "Default" : isNaN(selectedDocumentRevisionNumber?.value?.slice(1)) ? null : parseInt(selectedDocumentRevisionNumber?.value?.slice(1), 10),
        is_approved_document: selectedTab === 1,
        view_document: true
      },
      signal: abortController.signal
    })
      .then((response) => {
        setIsLoading(false);
        const { status, data } = response;

        if (status === 200) {
          window.open(data, '_blank', 'noopener, noreferrer');
        }
        else if (status === 202) {
          setAlertMessage(data?.data);
          setAlertType("info");
          setShowAlert(true);
        }
        else {
          setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
          setAlertType("error");
          setShowAlert(true);
        }
      })
      .catch((error) => {
        console.log("View Document Api: ", error);
        setIsLoading(false);

        if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
          return;
        }

        const status = error?.response?.status;

        if (status === 403) {
          triggerSessionExpire();
        }
        else {
          setAlertMessage(
            status === 401
              ? "Unauthorized access. You do not have the required permissions to perform this action."
              : status === 429
                ? "Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator."
                : "An error occurred while processing your request. Please try again later or contact the site administrator."
          );
          setAlertType("error");
          setShowAlert(true);

          if (status === 429) {
            setTimeout(logOut, 3000);
          }
        }
      });
  }

  const fetchLinkedTrancheCoos = async () => {
    if (!linkedTranche?.id) return;

    setIsLoadingLinkedCoos(true);
    try {
      const response = await axios({
        method: "post",
        url: "/api/GetCertificatesOfOrigin",
        data: {
          Business_Unit_Id: selectedBusinessUnit,
          Tranche_Id: linkedTranche.id
        },
        signal: abortController.signal
      });

      if (response.status === 200) {
        setLinkedTrancheCoos(response.data);
      }
    } catch (error) {
      console.log("Fetch Linked Tranche COOs Error: ", error);

      if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
        return;
      }

      const status = error?.response?.status;
      if (status === 403) {
        triggerSessionExpire();
      } else if (status === 429) {
        setAlertMessage("Request limit exceeded. Your account is temporarily disabled. Please contact the site administrator.");
        setAlertType("error");
        setShowAlert(true);
        setTimeout(logOut, 3000);
      }
    } finally {
      setIsLoadingLinkedCoos(false);
    }
  };

  const handleShowDocumentModal = (documentType, downloadButtonRef, linkedDownloadButtonRef = null) => {
    if (isUpdated) {
      setIsDocumentToBeGenerated(true);
      setShowConfirmationDialog(true);
      return;
    }

    setToggleDocumentList(!toggleDocumentList);
    setIsDocumentToBeGenerated(false);
    setShowAlert(false);
    setAlertMessage("");
    setAlertType("");
    setDocumentType(documentType);

    if (documentType !== "COO") {
      // ... existing code for non-COO documents
      const selectedDocumentRevisionNumbers = [
        ...new Set(
          documentRevisionNumbers
            .filter(documentRevisionNumber =>
              documentRevisionNumber.Document_Type === documentType &&
              (documentType === "PFI" || documentRevisionNumber.Tranche_Id === selectedTranche?.id)
            )
        )
      ] || [];
      const {
        is_document_generated: isDocumentGenerated = false,
        is_document_approved: isDocumentApproved = false
      } = documentGenerationData[documentType] || {};

      if (selectedDocumentRevisionNumbers.length > 0 || isDocumentGenerated) {
        const approvedDocumentRevisionNumbers = selectedDocumentRevisionNumbers.filter(documentRevisionNumber => documentRevisionNumber.isApproved);

        if (isDocumentGenerated) {
          selectedDocumentRevisionNumbers.unshift({ id: "Default", value: "Default" });

          if (isDocumentApproved) {
            approvedDocumentRevisionNumbers.unshift({ id: "Default", value: "Default" });
          }
        }

        setDraftDocuments(selectedDocumentRevisionNumbers);
        setApprovedDocuments(approvedDocumentRevisionNumbers);
      }

      setSelectedDocumentGenerationButtonRef(downloadButtonRef);
      setSelectedLinkedDocumentGenerationButtonRef(linkedDownloadButtonRef);
      setShowDocumentModal(true);
    }
    else {
      const approvedCertificateDocuments = coos.filter(coo => coo.isApproved);

      setDraftDocuments(coos);
      setApprovedDocuments(approvedCertificateDocuments);
      setSelectedDocumentGenerationButtonRef(downloadButtonRef);
      setSelectedLinkedDocumentGenerationButtonRef(linkedDownloadButtonRef);

      // Fetch linked tranche COOs if linked through route
      if (isLinkedThroughRoute && linkedTranche?.id) {
        fetchLinkedTrancheCoos();
      }

      setShowDocumentModal(true);
    }
  }

  // UPDATED handleGenerateDocument function for Documentation.jsx
  // Replace the existing handleGenerateDocument function with this version

  // SIMPLIFIED APPROACH - Replace handleGenerateDocument in Documentation.jsx
  // This version uses a callback-based approach that's more reliable

  const handleGenerateDocument = async () => {
    // Track main document completion
    let mainDocumentSuccess = false;
    let linkedDocumentSuccess = false;
    let shouldGenerateLinked = false;

    handleCloseModal(false); // Close modal but keep states for potential linked document generation

    // Determine order: FZE always first, Vantira always second
    const firstRef = isLinkedThroughRoute
      ? (mainEntityType === "fze" ? selectedDocumentGenerationButtonRef : selectedLinkedDocumentGenerationButtonRef)
      : selectedDocumentGenerationButtonRef;

    const secondRef = isLinkedThroughRoute
      ? (mainEntityType === "fze" ? selectedLinkedDocumentGenerationButtonRef : selectedDocumentGenerationButtonRef)
      : selectedLinkedDocumentGenerationButtonRef;

    // Create a promise that will be resolved when main document completes
    const mainDocumentPromise = new Promise((resolve) => {
      // Override the ref's click to track completion
      const originalRef = firstRef?.current;

      if (!originalRef) {
        console.error('Main document ref not found');
        resolve(false);
        return;
      }

      // Check if generateDocument method exists
      if (typeof originalRef.generateDocument === 'function') {
        originalRef.generateDocument()
          .then((success) => {
            mainDocumentSuccess = success;
            resolve(success);
          })
          .catch((error) => {
            console.error('Main document generation error:', error);
            resolve(false);
          });
      } else {
        // Fallback: just click and wait a bit
        originalRef.click();

        // Wait for a reasonable timeout
        setTimeout(() => {
          resolve(true); // Optimistically assume success
        }, 5000);
      }
    });

    try {
      // Wait for main document to complete (with 60s timeout)
      const timeoutPromise = new Promise((resolve) =>
        setTimeout(() => {
          resolve(false);
        }, 60000)
      );

      const success = await Promise.race([mainDocumentPromise, timeoutPromise]);
      mainDocumentSuccess = success;

      // Only proceed with linked document if main document succeeded
      if (success) {
        const requiresLinkedTranche = documentType !== "PFI";
        shouldGenerateLinked =
          isLinkedThroughRoute &&
          linkedPFI?.id &&
          secondRef?.current &&
          (!requiresLinkedTranche || linkedTranche?.id);

        if (shouldGenerateLinked) {
          // Small delay before generating linked document
          await new Promise(resolve => setTimeout(resolve, 1000));

          const linkedRef = secondRef?.current;

          if (linkedRef) {
            if (typeof linkedRef.generateDocument === 'function') {
              linkedDocumentSuccess = await linkedRef.generateDocument();
              console.log('Linked document generation result:', linkedDocumentSuccess);
            } else {
              linkedRef.click();
              // Optimistically assume success for fallback
              linkedDocumentSuccess = true;
            }
          }
        }
      }

      // Decision logic for handleCloseModal
      if (!shouldGenerateLinked) {
        // No linked document needed - close if main succeeded
        if (mainDocumentSuccess) {
          handleCloseModal(true);
          setToggleDocumentList(!toggleDocumentList);
          setToggleFileList(prev => !prev);
        } else {
          handleCloseModal(false);
        }
      } else {
        // Linked document was needed - close only if both succeeded
        if (mainDocumentSuccess && linkedDocumentSuccess) {
          setToggleDocumentList(!toggleDocumentList);
          setToggleFileList(prev => !prev);
          handleCloseModal(true);
        } else {
          handleCloseModal(false);
        }
      }

      console.log('Document generation summary:', {
        shouldGenerateLinked,
        mainDocumentSuccess,
        linkedDocumentSuccess,
        modalClosed: (!shouldGenerateLinked && mainDocumentSuccess) || (shouldGenerateLinked && mainDocumentSuccess && linkedDocumentSuccess)
      });

    } catch (error) {
      console.error('Error during document generation:', error);
      handleCloseModal(false);
    } finally {
      console.log('=== Document Generation Complete ===');

      // Trigger file list refresh after generation
    }
  };

  const handleCloseModal = (resetStates = true) => { // Changed default to false
    setShowDocumentModal(false);
    console.log(resetStates, 'resetStates');

    if (resetStates) {
      setDraftDocuments([]);
      setApprovedDocuments([]);
      setSelectedDocumentRevisionNumber(null);
      setAddTermsAndConditionsPage(false);
      setSelectedTab(0);
      setSelectedLinkedCOO(null);
      setSelectedCOO(null);
      setLinkedTrancheCoos([]); // Reset linked COOs
    }
  }

  const formSectionsHTML = [
    {
      id: 5,
      columns: 12,
      title: "Special Requirements",
      content: (
        <Grid
          container
          spacing={1}
          className="align-items-start"
        >
          <Grid size={{ xs: 4 }} className="form-text-field">
            <TextField
              label="SI Special Requirement"
              id="Special_Requirement_SI"
              disabled={Boolean(selectedTranche?.period_lock && isFieldLocked("Special_Requirement_SI", "Documentation", "Post-Shipment", lockedFields))}
              multiline
              rows={3}
              value={formData?.Special_Requirement_SI || ""}
              onChange={(event) => handleChange(event, "Special_Requirement_SI", event.target.value)}
              slotProps={{
                input: {
                  readOnly: !isWritable
                }
              }}
            />
          </Grid>

          <Grid size={{ xs: 4 }} className="form-text-field">
            <TextField
              label="PL Special Requirement"
              id="Special_Requirement_PL"
              disabled={Boolean(selectedTranche?.period_lock && isFieldLocked("Special_Requirement_PL", "Documentation", "Post-Shipment", lockedFields))}
              multiline
              rows={3}
              value={formData?.Special_Requirement_PL || ""}
              onChange={(event) => handleChange(event, "Special_Requirement_PL", event.target.value)}
              slotProps={{
                input: {
                  readOnly: !isWritable
                }
              }}
            />
          </Grid>

          <Grid size={{ xs: 4 }} className="form-text-field">
            <TextField
              label="CI Special Requirement"
              id="Special_Requirement_CI"
              disabled={Boolean(selectedTranche?.period_lock && isFieldLocked("Special_Requirement_CI", "Documentation", "Post-Shipment", lockedFields))}
              multiline
              rows={3}
              value={formData?.Special_Requirement_CI || ""}
              onChange={(event) => handleChange(event, "Special_Requirement_CI", event.target.value)}
              slotProps={{
                input: {
                  readOnly: !isWritable
                }
              }}
            />
          </Grid>

          <Grid size={{ xs: 4 }} className="form-text-field">
            <TextField
              label="BE Special Requirement"
              id="Special_Requirement_BE"
              disabled={Boolean(selectedTranche?.period_lock && isFieldLocked("Special_Requirement_BE", "Documentation", "Post-Shipment", lockedFields))}
              multiline
              rows={3}
              value={formData?.Special_Requirement_BE || ""}
              onChange={(event) => handleChange(event, "Special_Requirement_BE", event.target.value)}
              slotProps={{
                input: {
                  readOnly: !isWritable
                }
              }}
            />
          </Grid>

          <Grid size={{ xs: 4 }} className="form-text-field">
            <TextField
              label="SA Special Requirement"
              id="Special_Requirement_SA"
              disabled={Boolean(selectedTranche?.period_lock && isFieldLocked("Special_Requirement_SA", "Documentation", "Post-Shipment", lockedFields))}
              multiline
              rows={3}
              value={formData?.Special_Requirement_SA || ""}
              onChange={(event) => handleChange(event, "Special_Requirement_SA", event.target.value)}
              slotProps={{
                input: {
                  readOnly: !isWritable
                }
              }}
            />
          </Grid>
        </Grid>
      )
    },
    {
      id: 6,
      columns: 12,
      title: "CI Freight And Discount Adjustments",
      content: (
        <Grid
          container
          spacing={"10px"}
          className="align-items-start"
        >
          <Grid size={{ xs: 3 }} className="form-text-field">
            <TextField
              label="Advance Amount"
              id="Advance_Amount"
              disabled={Boolean(selectedTranche?.period_lock && isFieldLocked("Advance_Amount", "Documentation", "Post-Shipment", lockedFields))}
              type="number"
              value={formData?.Advance_Amount || ""}
              onChange={(event) => handleChange(event, "Advance_Amount", event.target.value)}
              size="small"
              slotProps={{
                input: {
                  readOnly: !isWritable,
                  step: "0.001",
                  endAdornment: (
                    <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                      {Boolean((selectedTranche?.period_lock && isFieldLocked("Advance_Amount", "Documentation", "Post-Shipment", lockedFields))) && (
                        <Tooltip
                          title="This field is locked and cannot be edited due to period lock."
                          arrow
                          placement="top"
                        >
                          <InfoIcon
                            fontSize="small"
                            sx={{ cursor: 'default' }} // No margins
                          />
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                  sx: {
                    padding: '5px', // Apply padding: 5px to the <input> element
                  },
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <TextField
              label="Total Amount"
              id="Total_Amount"
              type="number"
              value={formData?.Total_Amount || ""}
              disabled
              slotProps={{
                input: {
                  readOnly: !isWritable,
                  step: "0.001",
                },
                inputLabel: {
                  shrink: formData?.Total_Amount
                }
              }}
            />
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <TextField
              inputRef={fobValueInputRef}
              label="FOB Value"
              disabled={Boolean(selectedTranche?.period_lock && isFieldLocked("FOB_Value", "Documentation", "Post-Shipment", lockedFields))}
              id="FOB_Value"
              type="number"
              value={formData?.FOB_Value || ""}
              onChange={(event) => handleChange(event, "FOB_Value", event.target.value)}
              size="small"
              slotProps={{
                input: {
                  readOnly: !isWritable,
                  step: "0.001",
                  endAdornment: (
                    <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                      {Boolean((selectedTranche?.period_lock && isFieldLocked("FOB_Value", "Documentation", "Post-Shipment", lockedFields))) && (
                        <Tooltip
                          title="This field is locked and cannot be edited due to period lock."
                          arrow
                          placement="top"
                        >
                          <InfoIcon
                            fontSize="small"
                            sx={{ cursor: 'default' }} // No margins
                          />
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                  sx: {
                    padding: '5px', // Apply padding: 5px to the <input> element
                  },
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <TextField
              inputRef={freightInputRef}
              label="Freight"
              disabled={Boolean(selectedTranche?.period_lock && isFieldLocked("Freight", "Documentation", "Post-Shipment", lockedFields))}
              id="Freight"
              type="number"
              value={formData?.Freight || ""}
              onChange={(event) => handleChange(event, "Freight", event.target.value)}
              size="small"
              slotProps={{
                input: {
                  readOnly: !isWritable,
                  step: "0.001",
                  endAdornment: (
                    <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                      {Boolean((selectedTranche?.period_lock && isFieldLocked("Freight", "Documentation", "Post-Shipment", lockedFields))) && (
                        <Tooltip
                          title="This field is locked and cannot be edited due to period lock."
                          arrow
                          placement="top"
                        >
                          <InfoIcon
                            fontSize="small"
                            sx={{ cursor: 'default' }} // No margins
                          />
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                  sx: {
                    padding: '5px', // Apply padding: 5px to the <input> element
                  },
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <TextField
              label="Discount"
              id="Discount"
              disabled={Boolean(selectedTranche?.period_lock && isFieldLocked("Discount", "Documentation", "Post-Shipment", lockedFields))}
              type="number"
              value={formData?.Discount || ""}
              onChange={(event) => handleChange(event, "Discount", event.target.value)}
              size="small"
              slotProps={{
                input: {
                  readOnly: !isWritable,
                  step: "0.001",
                  endAdornment: (
                    <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                      {Boolean((selectedTranche?.period_lock && isFieldLocked("Discount", "Documentation", "Post-Shipment", lockedFields))) && (
                        <Tooltip
                          title="This field is locked and cannot be edited due to period lock."
                          arrow
                          placement="top"
                        >
                          <InfoIcon
                            fontSize="small"
                            sx={{ cursor: 'default' }} // No margins
                          />
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                  sx: {
                    padding: '5px', // Apply padding: 5px to the <input> element
                  },
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <TextField
              label="Label"
              id="Label"
              disabled={Boolean(selectedTranche?.period_lock && isFieldLocked("Label", "Documentation", "Post-Shipment", lockedFields))}
              value={formData?.Label || ""}
              onChange={(event) => handleChange(event, "Label", event.target.value)}
              size="small"
              slotProps={{
                input: {
                  readOnly: !isWritable,
                  endAdornment: (
                    <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                      {Boolean((selectedTranche?.period_lock && isFieldLocked("Label", "Documentation", "Post-Shipment", lockedFields))) && (
                        <Tooltip
                          title="This field is locked and cannot be edited due to period lock."
                          arrow
                          placement="top"
                        >
                          <InfoIcon
                            fontSize="small"
                            sx={{ cursor: 'default' }} // No margins
                          />
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                  sx: {
                    padding: '5px', // Apply padding: 5px to the <input> element
                  },
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <TextField
              label="Value"
              id="Value"
              disabled={Boolean(selectedTranche?.period_lock && isFieldLocked("Value", "Documentation", "Post-Shipment", lockedFields))}
              type="number"
              value={formData?.Value || ""}
              onChange={(event) => handleChange(event, "Value", event.target.value)}
              size="small"
              slotProps={{
                input: {
                  readOnly: !isWritable,
                  step: "0.001",
                  endAdornment: (
                    <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                      {Boolean((selectedTranche?.period_lock && isFieldLocked("Value", "Documentation", "Post-Shipment", lockedFields))) && (
                        <Tooltip
                          title="This field is locked and cannot be edited due to period lock."
                          arrow
                          placement="top"
                        >
                          <InfoIcon
                            fontSize="small"
                            sx={{ cursor: 'default' }} // No margins
                          />
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                  sx: {
                    padding: '5px', // Apply padding: 5px to the <input> element
                  },
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 3 }}>
            <VirtualizedAutocomplete
              isMultiple={false}
              isObject={false}
              isWritable={isWritable}
              isLocked={Boolean(selectedTranche?.period_lock && isFieldLocked("Transaction_Type", "Documentation", "Post-Shipment", lockedFields))}
              filterOn="Transaction Type"
              options={transactionTypes}
              selectedOptions={formData?.Transaction_Type || null}
              handleSelectChange={(filterOn, newValue) => handleChange(null, "Transaction_Type", newValue)}
            />
          </Grid>
        </Grid>
      )
    },
    {
      id: 7,
      columns: 12,
      title: "Documentation Details",
      content: (
        <Grid
          container
          spacing={"10px"}
          className="align-items-start autocomplete"
        >
          <Grid size={{ xs: 3 }} className="form-text-field">
            <TextField
              label="AWB from Supplier"
              id="AWB_from_Supplier"
              disabled={Boolean(selectedTranche?.period_lock && isFieldLocked("AWB_from_Supplier", "Documentation", "Post-Shipment", lockedFields))}
              value={formData?.AWB_from_Supplier || ""}
              onChange={(event) => handleChange(event, "AWB_from_Supplier", event.target.value)}
              size="small"
              slotProps={{
                input: {
                  readOnly: !isWritable,
                  endAdornment: (
                    <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                      {Boolean((selectedTranche?.period_lock && isFieldLocked("AWB_from_Supplier", "Documentation", "Post-Shipment", lockedFields))) && (
                        <Tooltip
                          title="This field is locked and cannot be edited due to period lock."
                          arrow
                          placement="top"
                        >
                          <InfoIcon
                            fontSize="small"
                            sx={{ cursor: 'default' }} // No margins
                          />
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                  sx: {
                    padding: '5px', // Apply padding: 5px to the <input> element
                  },
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                inputFormat="MM/DD/YYYY"
                label="Supplier Document Receiving Date"
                disableMaskedInput
                readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("Supplier_Document_Receiving_Date", "Documentation", "Post-Shipment", lockedFields))}
                className="date-picker w-100"
                value={convertToDayJsObject(formData?.Supplier_Document_Receiving_Date)}
                onChange={(newDate) => handleChange(null, "Supplier_Document_Receiving_Date", newDate, true)}
                onAccept={(newDate) => handleChange(null, "Supplier_Document_Receiving_Date", newDate, true)}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disabled: true,
                        endAdornment: (
                          <>
                            {Boolean((selectedTranche?.period_lock && isFieldLocked("Supplier_Document_Receiving_Date", "Documentation", "Post-Shipment", lockedFields))) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className='MuiSvgIcon-info'
                                  sx={{ marginRight: "0", cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  ),
                }}
                slotProps={{
                  actionBar: {
                    actions: ["clear"]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <TextField
              label="AWB to Customer"
              id="AWB_To_Customer"
              disabled={Boolean(selectedTranche?.period_lock && isFieldLocked("AWB_To_Customer", "Documentation", "Post-Shipment", lockedFields))}
              value={formData?.AWB_To_Customer || ""}
              onChange={(event) => handleChange(event, "AWB_To_Customer", event.target.value)}
              size="small"
              slotProps={{
                input: {
                  readOnly: !isWritable,
                  endAdornment: (
                    <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                      {Boolean((selectedTranche?.period_lock && isFieldLocked("AWB_To_Customer", "Documentation", "Post-Shipment", lockedFields))) && (
                        <Tooltip
                          title="This field is locked and cannot be edited due to period lock."
                          arrow
                          placement="top"
                        >
                          <InfoIcon
                            fontSize="small"
                            sx={{ cursor: 'default' }} // No margins
                          />
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                  sx: {
                    padding: '5px', // Apply padding: 5px to the <input> element
                  },
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                inputFormat="MM/DD/YYYY"
                label="Dispatch Date"
                disableMaskedInput
                readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("Dispatch_Date", "Documentation", "Post-Shipment", lockedFields))}
                className="date-picker w-100"
                value={convertToDayJsObject(formData?.Dispatch_Date)}
                onChange={(newDate) => handleChange(null, "Dispatch_Date", newDate, true)}
                onAccept={(newDate) => handleChange(null, "Dispatch_Date", newDate, true)}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disabled: true,
                        endAdornment: (
                          <>
                            {Boolean((selectedTranche?.period_lock && isFieldLocked("Dispatch_Date", "Documentation", "Post-Shipment", lockedFields))) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className='MuiSvgIcon-info'
                                  sx={{ marginRight: "0", cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  ),
                }}
                slotProps={{
                  actionBar: {
                    actions: ["clear"]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <TextField
              label="Disc. Acceptance"
              id="Disc_Acceptance"
              disabled={Boolean(selectedTranche?.period_lock && isFieldLocked("Disc_Acceptance", "Documentation", "Post-Shipment", lockedFields))}
              value={formData?.Disc_Acceptance || ""}
              onChange={(event) => handleChange(event, "Disc_Acceptance", event.target.value)}
              size="small"
              slotProps={{
                input: {
                  readOnly: !isWritable,
                  endAdornment: (
                    <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                      {Boolean((selectedTranche?.period_lock && isFieldLocked("Disc_Acceptance", "Documentation", "Post-Shipment", lockedFields))) && (
                        <Tooltip
                          title="This field is locked and cannot be edited due to period lock."
                          arrow
                          placement="top"
                        >
                          <InfoIcon
                            fontSize="small"
                            sx={{ cursor: 'default' }} // No margins
                          />
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                  sx: {
                    padding: '5px', // Apply padding: 5px to the <input> element
                  },
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                inputFormat="MM/DD/YYYY"
                label="Supplier Advance Date"
                disableMaskedInput
                readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("Advance_Payment_Date", "Documentation", "Post-Shipment", lockedFields))}
                className="date-picker w-100"
                value={convertToDayJsObject(formData?.Advance_Payment_Date)}
                onChange={(newDate) => handleChange(null, "Advance_Payment_Date", newDate, true)}
                onAccept={(newDate) => handleChange(null, "Advance_Payment_Date", newDate, true)}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disabled: true,
                        endAdornment: (
                          <>
                            {Boolean((selectedTranche?.period_lock && isFieldLocked("Advance_Payment_Date", "Documentation", "Post-Shipment", lockedFields))) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className='MuiSvgIcon-info'
                                  sx={{ marginRight: "0", cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  ),
                }}
                slotProps={{
                  actionBar: {
                    actions: ["clear"]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                inputFormat="MM/DD/YYYY"
                label="Customer Advance Payment Date"
                disableMaskedInput
                readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("Advance_Payment_Receive_Date", "Documentation", "Post-Shipment", lockedFields))}
                className="date-picker w-100"
                value={convertToDayJsObject(formData?.Advance_Payment_Receive_Date)}
                onChange={(newDate) => handleChange(null, "Advance_Payment_Receive_Date", newDate, true)}
                onAccept={(newDate) => handleChange(null, "Advance_Payment_Receive_Date", newDate, true)}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disabled: true,
                        endAdornment: (
                          <>
                            {Boolean((selectedTranche?.period_lock && isFieldLocked("Advance_Payment_Receive_Date", "Documentation", "Post-Shipment", lockedFields))) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className='MuiSvgIcon-info'
                                  sx={{ marginRight: "0", cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  ),
                }}
                slotProps={{
                  actionBar: {
                    actions: ["clear"]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                inputFormat="MM/DD/YYYY"
                label="Supplier Final Payment Date"
                disableMaskedInput
                readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("Final_Payment_Date", "Documentation", "Post-Shipment", lockedFields))}
                className="date-picker w-100"
                value={convertToDayJsObject(formData?.Final_Payment_Date)}
                onChange={(newDate) => handleChange(null, "Final_Payment_Date", newDate, true)}
                onAccept={(newDate) => handleChange(null, "Final_Payment_Date", newDate, true)}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disabled: true,
                        endAdornment: (
                          <>
                            {Boolean((selectedTranche?.period_lock && isFieldLocked("Final_Payment_Date", "Documentation", "Post-Shipment", lockedFields))) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className='MuiSvgIcon-info'
                                  sx={{ marginRight: "0", cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  ),
                }}
                slotProps={{
                  actionBar: {
                    actions: ["clear"]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                inputFormat="MM/DD/YYYY"
                label="Customer Final Payment Receive Date"
                disableMaskedInput
                readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("Final_Payment_Receive_Date", "Documentation", "Post-Shipment", lockedFields))}
                className="date-picker w-100"
                value={convertToDayJsObject(formData?.Final_Payment_Receive_Date)}
                onChange={(newDate) => handleChange(null, "Final_Payment_Receive_Date", newDate, true)}
                onAccept={(newDate) => handleChange(null, "Final_Payment_Receive_Date", newDate, true)}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disabled: true,
                        endAdornment: (
                          <>
                            {Boolean((selectedTranche?.period_lock && isFieldLocked("Final_Payment_Receive_Date", "Documentation", "Post-Shipment", lockedFields))) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className='MuiSvgIcon-info'
                                  sx={{ marginRight: "0", cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  ),
                }}
                slotProps={{
                  actionBar: {
                    actions: ["clear"]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          {(formData?.Selling_Payment_Term === "LC" && formData?.Selling_Tenor !== "Sight") && (
            <Grid size={{ xs: 3 }} className="form-text-field">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                  inputFormat="MM/DD/YYYY"
                  label="Maturity Date for Customer"
                  disableMaskedInput
                  className="date-picker w-100"
                  disabled={true}
                  value={calculateMaturityDate("Customer")}
                  slotProps={{
                    textField: {
                      InputProps: {
                        size: "small"
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
          )}

          {(formData?.Buying_Payment_Term === "LC" && formData?.Buying_Tenor !== "Sight") && (
            <Grid size={{ xs: 3 }} className="form-text-field">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                  inputFormat="MM/DD/YYYY"
                  label="Maturity Date for Supplier"
                  disableMaskedInput
                  className="date-picker w-100"
                  disabled={true}
                  value={calculateMaturityDate("Supplier")}
                  slotProps={{
                    textField: {
                      InputProps: {
                        size: "small"
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
          )}

          <Grid size={{ xs: 3 }}>
            <VirtualizedAutocomplete
              isMultiple={false}
              isObject={false}
              isWritable={isWritable}
              isLocked={Boolean(selectedTranche?.period_lock && isFieldLocked("Post_Pre_Discounting", "Documentation", "Post-Shipment", lockedFields))}
              filterOn="Pre Post Discounting"
              options={['Pre', 'Post']}
              selectedOptions={formData?.Post_Pre_Discounting || null}
              handleSelectChange={(filterOn, newValue) => handleChange(null, "Post_Pre_Discounting", newValue)}
            />
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <TextField
              label="Customer Transaction Status"
              id="Customer_Transaction_Status"
              disabled={Boolean(selectedTranche?.period_lock && isFieldLocked("Customer_Transaction_Status", "Documentation", "Post-Shipment", lockedFields))}
              value={formData?.Customer_Transaction_Status || ""}
              onChange={(event) => handleChange(event, "Customer_Transaction_Status", event.target.value)}
              size="small"
              slotProps={{
                input: {
                  readOnly: !isWritable,
                  endAdornment: (
                    <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                      {Boolean((selectedTranche?.period_lock && isFieldLocked("Customer_Transaction_Status", "Documentation", "Post-Shipment", lockedFields))) && (
                        <Tooltip
                          title="This field is locked and cannot be edited due to period lock."
                          arrow
                          placement="top"
                        >
                          <InfoIcon
                            fontSize="small"
                            sx={{ cursor: 'default' }} // No margins
                          />
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                  sx: {
                    padding: '5px', // Apply padding: 5px to the <input> element
                  },
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <TextField
              label="Supplier Transaction Status"
              id="Supplier_Transaction_Status"
              disabled={Boolean(selectedTranche?.period_lock && isFieldLocked("Supplier_Transaction_Status", "Documentation", "Post-Shipment", lockedFields))}
              value={formData?.Supplier_Transaction_Status || ""}
              onChange={(event) => handleChange(event, "Supplier_Transaction_Status", event.target.value)}
              size="small"
              slotProps={{
                input: {
                  readOnly: !isWritable,
                  endAdornment: (
                    <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                      {Boolean((selectedTranche?.period_lock && isFieldLocked("Supplier_Transaction_Status", "Documentation", "Post-Shipment", lockedFields))) && (
                        <Tooltip
                          title="This field is locked and cannot be edited due to period lock."
                          arrow
                          placement="top"
                        >
                          <InfoIcon
                            fontSize="small"
                            sx={{ cursor: 'default' }} // No margins
                          />
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                  sx: {
                    padding: '5px', // Apply padding: 5px to the <input> element
                  },
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 3 }}>
            <VirtualizedAutocomplete
              isLoading={banks?.length === 0}
              isMultiple={false}
              isObject={true}
              isWritable={isWritable}
              isLocked={Boolean(selectedTranche?.period_lock && isFieldLocked("Discounting_Bank_Id", "Documentation", "Post-Shipment", lockedFields))}
              filterOn="Export Discounting Bank"
              options={banks}
              selectedOptions={formData?.Discounting_Bank_Id || null}
              handleSelectChange={(filterOn, newValue) => handleChange(null, "Discounting_Bank_Id", newValue)}
            />
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                inputFormat="MM/DD/YYYY"
                label="Export Docs Received Date"
                disableMaskedInput
                readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("Export_Docs_Received_Date", "Documentation", "Post-Shipment", lockedFields))}
                className="date-picker w-100"
                value={convertToDayJsObject(formData?.Export_Docs_Received_Date)}
                onChange={(newDate) => handleChange(null, "Export_Docs_Received_Date", newDate, true)}
                onAccept={(newDate) => handleChange(null, "Export_Docs_Received_Date", newDate, true)}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disabled: true,
                        endAdornment: (
                          <>
                            {Boolean((selectedTranche?.period_lock && isFieldLocked("Export_Docs_Received_Date", "Documentation", "Post-Shipment", lockedFields))) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className='MuiSvgIcon-info'
                                  sx={{ marginRight: "0", cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  ),
                }}
                slotProps={{
                  actionBar: {
                    actions: ["clear"]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                inputFormat="MM/DD/YYYY"
                label="Export Docs Submission Date"
                disableMaskedInput
                readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("Export_Docs_Submission_Date", "Documentation", "Post-Shipment", lockedFields))}
                className="date-picker w-100"
                value={convertToDayJsObject(formData?.Export_Docs_Submission_Date)}
                onChange={(newDate) => handleChange(null, "Export_Docs_Submission_Date", newDate, true)}
                onAccept={(newDate) => handleChange(null, "Export_Docs_Submission_Date", newDate, true)}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disabled: true,
                        endAdornment: (
                          <>
                            {Boolean((selectedTranche?.period_lock && isFieldLocked("Export_Docs_Submission_Date", "Documentation", "Post-Shipment", lockedFields))) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className='MuiSvgIcon-info'
                                  sx={{ marginRight: "0", cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  ),
                }}
                slotProps={{
                  actionBar: {
                    actions: ["clear"]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                inputFormat="MM/DD/YYYY"
                label="Customer Due Date"
                disableMaskedInput
                readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("Due_Date", "Documentation", "Post-Shipment", lockedFields))}
                className="date-picker w-100"
                value={convertToDayJsObject(formData?.Due_Date)}
                onChange={(newDate) => handleChange(null, "Due_Date", newDate, true)}
                onAccept={(newDate) => handleChange(null, "Due_Date", newDate, true)}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disabled: true,
                        endAdornment: (
                          <>
                            {Boolean((selectedTranche?.period_lock && isFieldLocked("Due_Date", "Documentation", "Post-Shipment", lockedFields))) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className='MuiSvgIcon-info'
                                  sx={{ marginRight: "0", cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  ),
                }}
                slotProps={{
                  actionBar: {
                    actions: ["clear"]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                inputFormat="MM/DD/YYYY"
                label="Supplier Due Date"
                disableMaskedInput
                readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("Supplier_Due_Date", "Documentation", "Post-Shipment", lockedFields))}
                className="date-picker w-100"
                value={convertToDayJsObject(formData?.Supplier_Due_Date)}
                onChange={(newDate) => handleChange(null, "Supplier_Due_Date", newDate, true)}
                onAccept={(newDate) => handleChange(null, "Supplier_Due_Date", newDate, true)}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disabled: true,
                        endAdornment: (
                          <>
                            {Boolean((selectedTranche?.period_lock && isFieldLocked("Supplier_Due_Date", "Documentation", "Post-Shipment", lockedFields))) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className='MuiSvgIcon-info'
                                  sx={{ marginRight: "0", cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  ),
                }}
                slotProps={{
                  actionBar: {
                    actions: ["clear"]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                inputFormat="MM/DD/YYYY"
                label="UPAS (Bank) Due Date"
                disableMaskedInput
                readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("UPAS_Bank_Due_Date", "Documentation", "Post-Shipment", lockedFields))}
                className="date-picker w-100"
                value={convertToDayJsObject(formData?.UPAS_Bank_Due_Date)}
                onChange={(newDate) => handleChange(null, "UPAS_Bank_Due_Date", newDate, true)}
                onAccept={(newDate) => handleChange(null, "UPAS_Bank_Due_Date", newDate, true)}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disabled: true,
                        endAdornment: (
                          <>
                            {Boolean((selectedTranche?.period_lock && isFieldLocked("UPAS_Bank_Due_Date", "Documentation", "Post-Shipment", lockedFields))) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className='MuiSvgIcon-info'
                                  sx={{ marginRight: "0", cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  ),
                }}
                slotProps={{
                  actionBar: {
                    actions: ["clear"]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                inputFormat="MM/DD/YYYY"
                label="TR Settlement Date"
                disableMaskedInput
                readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("TR_Settlement_Date", "Documentation", "Post-Shipment", lockedFields))}
                className="date-picker w-100"
                value={convertToDayJsObject(formData?.TR_Settlement_Date)}
                onChange={(newDate) => handleChange(null, "TR_Settlement_Date", newDate, true)}
                onAccept={(newDate) => handleChange(null, "TR_Settlement_Date", newDate, true)}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disabled: true,
                        endAdornment: (
                          <>
                            {Boolean((selectedTranche?.period_lock && isFieldLocked("TR_Settlement_Date", "Documentation", "Post-Shipment", lockedFields))) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className='MuiSvgIcon-info'
                                  sx={{ marginRight: "0", cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  ),
                }}
                slotProps={{
                  actionBar: {
                    actions: ["clear"]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <TextField
              label="Funds Received From"
              id="Funds_Received_From"
              disabled={Boolean(selectedTranche?.period_lock && isFieldLocked("Funds_Received_From", "Documentation", "Post-Shipment", lockedFields))}
              value={formData?.Funds_Received_From || ""}
              onChange={(event) => handleChange(event, "Funds_Received_From", event.target.value)}
              size="small"
              slotProps={{
                input: {
                  readOnly: !isWritable,
                  endAdornment: (
                    <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                      {Boolean(selectedTranche?.period_lock && isFieldLocked("Funds_Received_From", "Documentation", "Post-Shipment", lockedFields)) && (
                        <Tooltip
                          title="This field is locked and cannot be edited due to period lock."
                          arrow
                          placement="top"
                        >
                          <InfoIcon
                            fontSize="small"
                            sx={{ cursor: 'default' }}
                          />
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 4 }} className="form-text-field">
            <TextField
              label="Comments"
              id="Special_Requirement"
              disabled={Boolean(selectedTranche?.period_lock && isFieldLocked("Special_Requirement", "Documentation", "Post-Shipment", lockedFields))}
              multiline
              rows={3}
              value={formData?.Special_Requirement || ""}
              onChange={(event) => handleChange(event, "Special_Requirement", event.target.value)}
              slotProps={{
                input: {
                  readOnly: !isWritable
                }
              }}
            />
          </Grid>
        </Grid>
      )
    },
    {
      id: 8,
      columns: 12,
      title: "Target Dates",
      content: (
        <Grid
          container
          spacing={"10px"}
          className="align-items-start autocomplete"
        >
          <Grid size={{ xs: 3 }} className="form-text-field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                inputFormat="MM/DD/YYYY"
                label="Target Customer PFI Issuance Date"
                disableMaskedInput
                readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("Target_Customer_PFI_Issuance_Date", "Documentation", "Post-Shipment", lockedFields))}
                className="date-picker w-100"
                value={convertToDayJsObject(formData?.Target_Customer_PFI_Issuance_Date)}
                onChange={(newDate) => handleChange(null, "Target_Customer_PFI_Issuance_Date", newDate, true)}
                onAccept={(newDate) => handleChange(null, "Target_Customer_PFI_Issuance_Date", newDate, true)}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disabled: true,
                        endAdornment: (
                          <>
                            {Boolean((selectedTranche?.period_lock && isFieldLocked("Target_Customer_PFI_Issuance_Date", "Documentation", "Post-Shipment", lockedFields))) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className='MuiSvgIcon-info'
                                  sx={{ marginRight: "0", cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  ),
                }}
                slotProps={{
                  actionBar: {
                    actions: ["clear"]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                inputFormat="MM/DD/YYYY"
                label="Target Customer NN Documents"
                disableMaskedInput
                readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("Target_Customer_NN_Documents", "Documentation", "Post-Shipment", lockedFields))}
                className="date-picker w-100"
                value={convertToDayJsObject(formData?.Target_Customer_NN_Documents)}
                onChange={(newDate) => handleChange(null, "Target_Customer_NN_Documents", newDate, true)}
                onAccept={(newDate) => handleChange(null, "Target_Customer_NN_Documents", newDate, true)}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disabled: true,
                        endAdornment: (
                          <>
                            {Boolean((selectedTranche?.period_lock && isFieldLocked("Target_Customer_NN_Documents", "Documentation", "Post-Shipment", lockedFields))) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className='MuiSvgIcon-info'
                                  sx={{ marginRight: "0", cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  ),
                }}
                slotProps={{
                  actionBar: {
                    actions: ["clear"]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                inputFormat="MM/DD/YYYY"
                label="Target Supplier Original Documents"
                disableMaskedInput
                readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("Target_Supplier_Original_Documents", "Documentation", "Post-Shipment", lockedFields))}
                className="date-picker w-100"
                value={convertToDayJsObject(formData?.Target_Supplier_Original_Documents)}
                onChange={(newDate) => handleChange(null, "Target_Supplier_Original_Documents", newDate, true)}
                onAccept={(newDate) => handleChange(null, "Target_Supplier_Original_Documents", newDate, true)}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disabled: true,
                        endAdornment: (
                          <>
                            {Boolean((selectedTranche?.period_lock && isFieldLocked("Target_Supplier_Original_Documents", "Documentation", "Post-Shipment", lockedFields))) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className='MuiSvgIcon-info'
                                  sx={{ marginRight: "0", cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  ),
                }}
                slotProps={{
                  actionBar: {
                    actions: ["clear"]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                inputFormat="MM/DD/YYYY"
                label="Customer LC Deadline"
                disableMaskedInput
                readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("Customer_LC_Deadline", "Documentation", "Post-Shipment", lockedFields))}
                className="date-picker w-100"
                value={convertToDayJsObject(formData?.Customer_LC_Deadline)}
                onChange={(newDate) => handleChange(null, "Customer_LC_Deadline", newDate, true)}
                onAccept={(newDate) => handleChange(null, "Customer_LC_Deadline", newDate, true)}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disabled: true,
                        endAdornment: (
                          <>
                            {Boolean((selectedTranche?.period_lock && isFieldLocked("Customer_LC_Deadline", "Documentation", "Post-Shipment", lockedFields))) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className='MuiSvgIcon-info'
                                  sx={{ marginRight: "0", cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  ),
                }}
                slotProps={{
                  actionBar: {
                    actions: ["clear"]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                inputFormat="MM/DD/YYYY"
                label="Target Supplier LC Issuance Date"
                disableMaskedInput
                readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("Target_Supplier_LC_Issuance_Date", "Documentation", "Post-Shipment", lockedFields))}
                className="date-picker w-100"
                value={convertToDayJsObject(formData?.Target_Supplier_LC_Issuance_Date)}
                onChange={(newDate) => handleChange(null, "Target_Supplier_LC_Issuance_Date", newDate, true)}
                onAccept={(newDate) => handleChange(null, "Target_Supplier_LC_Issuance_Date", newDate, true)}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disabled: true,
                        endAdornment: (
                          <>
                            {Boolean((selectedTranche?.period_lock && isFieldLocked("Target_Supplier_LC_Issuance_Date", "Documentation", "Post-Shipment", lockedFields))) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className='MuiSvgIcon-info'
                                  sx={{ marginRight: "0", cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  ),
                }}
                slotProps={{
                  actionBar: {
                    actions: ["clear"]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                inputFormat="MM/DD/YYYY"
                label="Target Supplier NN Documents"
                disableMaskedInput
                readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("Target_Supplier_NN_Documents", "Documentation", "Post-Shipment", lockedFields))}
                className="date-picker w-100"
                value={convertToDayJsObject(formData?.Target_Supplier_NN_Documents)}
                onChange={(newDate) => handleChange(null, "Target_Supplier_NN_Documents", newDate, true)}
                onAccept={(newDate) => handleChange(null, "Target_Supplier_NN_Documents", newDate, true)}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disabled: true,
                        endAdornment: (
                          <>
                            {Boolean((selectedTranche?.period_lock && isFieldLocked("Target_Supplier_NN_Documents", "Documentation", "Post-Shipment", lockedFields))) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className='MuiSvgIcon-info'
                                  sx={{ marginRight: "0", cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  ),
                }}
                slotProps={{
                  actionBar: {
                    actions: ["clear"]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid size={{ xs: 3 }} className="form-text-field">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                inputFormat="MM/DD/YYYY"
                label="Target Customer Original Documents"
                disableMaskedInput
                readOnly={!isWritable || (selectedTranche?.period_lock && isFieldLocked("Target_Customer_Original_Documents", "Documentation", "Post-Shipment", lockedFields))}
                className="date-picker w-100"
                value={convertToDayJsObject(formData?.Target_Customer_Original_Documents)}
                onChange={(newDate) => handleChange(null, "Target_Customer_Original_Documents", newDate, true)}
                onAccept={(newDate) => handleChange(null, "Target_Customer_Original_Documents", newDate, true)}
                enableAccessibleFieldDOMStructure={false}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      size="small"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        disabled: true,
                        endAdornment: (
                          <>
                            {Boolean((selectedTranche?.period_lock && isFieldLocked("Target_Customer_Original_Documents", "Documentation", "Post-Shipment", lockedFields))) && (
                              <Tooltip
                                title="This field is locked and cannot be edited due to period lock."
                                arrow
                                placement="top"
                              >
                                <InfoIcon
                                  fontSize="small"
                                  className='MuiSvgIcon-info'
                                  sx={{ marginRight: "0", cursor: 'default' }}
                                />
                              </Tooltip>
                            )}
                            {params.InputProps?.endAdornment}
                          </>
                        ),
                      }}
                    />
                  ),
                }}
                slotProps={{
                  actionBar: {
                    actions: ["clear"]
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      )
    },
    {
      id: 9,
      columns: 6,
      title: "Document Creation",
      content: (
        <>
          <Grid
            container
            spacing={"10px"}
            className="align-items-center autocomplete justify-content-center column-gap-2 document-generation-buttons"
          >
            <Grid size={{ xs: 6 }} className="d-flex form-text-field justify-content-center">
              {documentPermissions["PFI"]?.isReadable && (
                <ButtonGroup variant="contained">
                  <Button sx={{ cursor: 'default !important', width: '170px' }}>Generate PFI</Button>

                  <IconButton
                    color="primary"
                    disabled={!selectedPFI || isPFIDocumentGenerating}
                    onClick={() => handleShowDocumentModal("PFI", pfiDocumentDownloadButtonRef, linkedPfiDocumentDownloadButtonRef)}
                    sx={{
                      margin: '5px !important'
                    }}
                  >
                    {isPFIDocumentGenerating ? (
                      <CircularProgress color="inherit" />
                    ) : (
                      <FileDownloadIcon fontSize="small" />
                    )}
                  </IconButton>
                </ButtonGroup>
              )}
            </Grid>

            <Grid size={{ xs: 6 }} className="d-flex form-text-field justify-content-center">
              {documentPermissions["SI"]?.isReadable && (
                <ButtonGroup variant="contained" >
                  <Button sx={{ cursor: 'default !important', width: '170px' }}>Generate SI</Button>

                  <IconButton
                    color="primary"
                    disabled={!selectedTranche || isSIDocumentGenerating}
                    onClick={() => handleShowDocumentModal("SI", siDocumentDownloadButtonRef, linkedSiDocumentDownloadButtonRef)}
                    sx={{
                      margin: '5px !important'
                    }}
                  >
                    {isSIDocumentGenerating ? (
                      <CircularProgress color="inherit" />
                    ) : (
                      <FileDownloadIcon fontSize="small" />
                    )}
                  </IconButton>
                </ButtonGroup>
              )}
            </Grid>

            <Grid size={{ xs: 6 }} className="d-flex form-text-field justify-content-center">
              {documentPermissions["CI"]?.isReadable && (
                <ButtonGroup variant="contained" >
                  <Button sx={{ cursor: 'default !important', width: '170px' }}>Generate CI</Button>

                  <IconButton
                    color="primary"
                    disabled={!selectedTranche || isCIDocumentGenerating}
                    onClick={() => handleShowDocumentModal("CI", ciDocumentDownloadButtonRef, linkedCiDocumentDownloadButtonRef)}
                    sx={{
                      margin: '5px !important'
                    }}
                  >
                    {isCIDocumentGenerating ? (
                      <CircularProgress color="inherit" />
                    ) : (
                      <FileDownloadIcon fontSize="small" />
                    )}
                  </IconButton>
                </ButtonGroup>
              )}
            </Grid>

            <Grid size={{ xs: 6 }} className="d-flex form-text-field justify-content-center">
              {documentPermissions["SA"]?.isReadable && (
                <ButtonGroup variant="contained" >
                  <Button sx={{ cursor: 'default !important', width: '170px' }}>Generate SA</Button>

                  <IconButton
                    color="primary"
                    disabled={!selectedTranche || isSADocumentGenerating}
                    onClick={() => handleShowDocumentModal("SA", saDocumentDownloadButtonRef, linkedSaDocumentDownloadButtonRef)}
                    sx={{
                      margin: '5px !important'
                    }}
                  >
                    {isSADocumentGenerating ? (
                      <CircularProgress color="inherit" />
                    ) : (
                      <FileDownloadIcon fontSize="small" />
                    )}
                  </IconButton>
                </ButtonGroup>
              )}
            </Grid>

            <Grid size={{ xs: 6 }} className="d-flex form-text-field justify-content-center">
              {documentPermissions["PL"]?.isReadable && (
                <ButtonGroup variant="contained" >
                  <Button sx={{ cursor: 'default !important', width: '170px' }}>Generate PL</Button>

                  <IconButton
                    color="primary"
                    disabled={!selectedTranche || isPLDocumentGenerating}
                    onClick={() => handleShowDocumentModal("PL", plDocumentDownloadButtonRef, linkedPlDocumentDownloadButtonRef)}
                    sx={{
                      margin: '5px !important'
                    }}
                  >
                    {isPLDocumentGenerating ? (
                      <CircularProgress color="inherit" />
                    ) : (
                      <FileDownloadIcon fontSize="small" />
                    )}
                  </IconButton>
                </ButtonGroup>
              )}
            </Grid>

            <Grid size={{ xs: 6 }} className="d-flex form-text-field justify-content-center">
              {documentPermissions["BE"]?.isReadable && (
                <ButtonGroup variant="contained" >
                  <Button sx={{ cursor: 'default !important', width: '170px' }}>Generate BE</Button>

                  <IconButton
                    color="primary"
                    disabled={!selectedTranche || isBEDocumentGenerating}
                    onClick={() => handleShowDocumentModal("BE", beDocumentDownloadButtonRef, linkedBeDocumentDownloadButtonRef)}
                    sx={{
                      margin: '5px !important'
                    }}
                  >
                    {isBEDocumentGenerating ? (
                      <CircularProgress color="inherit" />
                    ) : (
                      <FileDownloadIcon fontSize="small" />
                    )}
                  </IconButton>
                </ButtonGroup>
              )}
            </Grid>

            <Grid size={{ xs: 6 }} className="d-flex form-text-field justify-content-center">
              {documentPermissions["COO"]?.isReadable && (
                <ButtonGroup variant="contained" >
                  <Button sx={{ cursor: 'default !important', width: '170px' }}>Other Certificates</Button>

                  <IconButton
                    color="primary"
                    disabled={!selectedTranche || isCOODocumentGenerating}
                    onClick={() => handleShowDocumentModal("COO", cooDocumentDownloadButtonRef, linkedCooDocumentDownloadButtonRef)}
                    sx={{
                      margin: '5px !important'
                    }}
                  >
                    {isCOODocumentGenerating ? (
                      <CircularProgress color="inherit" />
                    ) : (
                      <FileDownloadIcon fontSize="small" />
                    )}
                  </IconButton>
                </ButtonGroup>
              )}
            </Grid>

            <Grid size={{ xs: 6 }} className="d-flex form-text-field justify-content-center">
              {documentPermissions["Notify"]?.isReadable && (
                <DocumentReadyNotice
                  selectedBusinessUnit={selectedBusinessUnit}
                  selectedPFI={selectedPFI}
                  selectedTranche={selectedTranche}
                  isDisabled={!selectedTranche}
                  setShowAlert={setShowAlert}
                  setAlertMessage={setAlertMessage}
                  setAlertType={setAlertType}
                  toggleDocumentList={toggleDocumentList}
                  setToggleDocumentList={setToggleDocumentList}
                  setToggleFileList={setToggleFileList}
                  handleResetModal={handleCloseModal}
                  triggerSessionExpire={triggerSessionExpire}
                  logOut={logOut}
                />
              )}
            </Grid>
          </Grid>

          {/* Regular Document Downloaders */}
          <ReportDownloader
            ref={pfiDocumentDownloadButtonRef}
            apiMethod="post"
            api="/api/GeneratePFI"
            apiData={{
              Business_Unit_Id: selectedBusinessUnit,
              Deal_PFI_Id: selectedPFI,
              Revision_Number: selectedDocumentRevisionNumber?.value === "Default" ? "Default" : isNaN(selectedDocumentRevisionNumber?.value?.slice(1)) ? null : parseInt(selectedDocumentRevisionNumber?.value?.slice(1), 10),
              User_Name: currentUser?.name,
              User_Id: currentUser?.id,
              is_approved_document: selectedTab === 1,
              add_terms_and_conditions_page: addTermsAndConditionsPage,
              is_foods_and_polymers_deal: isFoodsOrPolymersDeal,
              is_sblc_deal: isSBLCDeal,
              commercial_invoice_vantira: Boolean(mainEntityType === "vantira") ? commmercialInvoiceNo : null,
              is_vantira_deal: Boolean(mainEntityType === "vantira") || false,
              linked_pfi_id: linkedPFI?.id
            }}
            fileName="PFI"
            buttonText="Generate PFI"
            toggleDocumentList={toggleDocumentList}
            setToggleDocumentList={setToggleDocumentList}
            setToggleFileList={setToggleFileList}
            handleResetModal={handleCloseModal}
            setIsLoading={setIsPFIDocumentGenerating}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setAlertType={setAlertType}

            isLinkedDocument={isLinkedThroughRoute && linkedPFI?.id}
            entityType={mainEntityType}
            onMissingFieldsDetected={handleMissingFieldsDetected}
          />

          {/* Linked PFI Document Downloader */}
          {isLinkedThroughRoute && linkedPFI?.id && (
            <ReportDownloader
              ref={linkedPfiDocumentDownloadButtonRef}
              apiMethod="post"
              api="/api/GeneratePFI"
              apiData={{
                Business_Unit_Id: selectedBusinessUnit,
                Deal_PFI_Id: linkedPFI?.id,
                Revision_Number: selectedDocumentRevisionNumber?.value === "Default" ? "Default" : isNaN(selectedDocumentRevisionNumber?.value?.slice(1)) ? null : parseInt(selectedDocumentRevisionNumber?.value?.slice(1), 10),
                User_Name: currentUser?.name,
                User_Id: currentUser?.id,
                is_approved_document: selectedTab === 1,
                add_terms_and_conditions_page: addTermsAndConditionsPage,
                is_foods_and_polymers_deal: isFoodsOrPolymersDeal,
                is_sblc_deal: isSBLCDeal,
                commercial_invoice_vantira: Boolean(linkedEntityType === "vantira") ? commmercialInvoiceNo : null,
                is_vantira_deal: Boolean(linkedEntityType === "vantira") || false,
                linked_pfi_id: selectedPFI
              }}
              fileName="PFI_Linked"
              buttonText="Generate Linked PFI"
              toggleDocumentList={toggleDocumentList}
              setToggleDocumentList={setToggleDocumentList}
              setToggleFileList={setToggleFileList}
              handleResetModal={handleCloseModal}
              setIsLoading={setIsPFIDocumentGenerating}
              setShowAlert={setShowAlert}
              setAlertMessage={setAlertMessage}
              setAlertType={setAlertType}

              isLinkedDocument={true}
              entityType={linkedEntityType}
              onMissingFieldsDetected={handleMissingFieldsDetected}
            />
          )}

          <ReportDownloader
            ref={siDocumentDownloadButtonRef}
            apiMethod="post"
            api="/api/GenerateSI"
            apiData={{
              Business_Unit_Id: selectedBusinessUnit,
              Deal_PFI_Id: selectedPFI,
              Tranche_Id: selectedTranche?.id,
              Revision_Number: selectedDocumentRevisionNumber?.value === "Default" ? "Default" : isNaN(selectedDocumentRevisionNumber?.value?.slice(1)) ? null : parseInt(selectedDocumentRevisionNumber?.value?.slice(1), 10),
              User_Name: currentUser?.name,
              User_Id: currentUser?.id,
              is_approved_document: selectedTab === 1,
              is_foods_and_polymers_deal: isFoodsOrPolymersDeal,
              is_sblc_deal: isSBLCDeal,
              commercial_invoice_vantira: Boolean(mainEntityType === "vantira") ? commmercialInvoiceNo : null,
              is_vantira_deal: Boolean(mainEntityType === "vantira") || false,
              linked_tranche_id: linkedTranche?.id
            }}
            fileName="SI"
            buttonText="Generate SI"
            isDisabled={!selectedTranche}
            toggleDocumentList={toggleDocumentList}
            setToggleDocumentList={setToggleDocumentList}
            setToggleFileList={setToggleFileList}
            handleResetModal={handleCloseModal}
            setIsLoading={setIsSIDocumentGenerating}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setAlertType={setAlertType}

            isLinkedDocument={isLinkedThroughRoute && linkedPFI?.id}
            entityType={mainEntityType}
            onMissingFieldsDetected={handleMissingFieldsDetected}
          />

          {/* Linked SI Document Downloader */}
          {isLinkedThroughRoute && linkedTranche?.id && linkedPFI?.id && (
            <ReportDownloader
              ref={linkedSiDocumentDownloadButtonRef}
              apiMethod="post"
              api="/api/GenerateSI"
              apiData={{
                Business_Unit_Id: selectedBusinessUnit,
                Deal_PFI_Id: linkedPFI?.id,
                Tranche_Id: linkedTranche?.id,
                Revision_Number: selectedDocumentRevisionNumber?.value === "Default" ? "Default" : isNaN(selectedDocumentRevisionNumber?.value?.slice(1)) ? null : parseInt(selectedDocumentRevisionNumber?.value?.slice(1), 10),
                User_Name: currentUser?.name,
                User_Id: currentUser?.id,
                is_approved_document: selectedTab === 1,
                is_foods_and_polymers_deal: isFoodsOrPolymersDeal,
                is_sblc_deal: isSBLCDeal,
                commercial_invoice_vantira: Boolean(linkedEntityType === "vantira") ? commmercialInvoiceNo : null,
                is_vantira_deal: Boolean(linkedEntityType === "vantira") || false,
                linked_tranche_id: selectedTranche?.id
              }}
              fileName="SI_Linked"
              buttonText="Generate Linked SI"
              isDisabled={!linkedTranche}
              toggleDocumentList={toggleDocumentList}
              setToggleDocumentList={setToggleDocumentList}
              setToggleFileList={setToggleFileList}
              handleResetModal={handleCloseModal}
              setIsLoading={setIsSIDocumentGenerating}
              setShowAlert={setShowAlert}
              setAlertMessage={setAlertMessage}
              setAlertType={setAlertType}

              isLinkedDocument={true}
              entityType={linkedEntityType}
              onMissingFieldsDetected={handleMissingFieldsDetected}
            />
          )}

          <ReportDownloader
            ref={ciDocumentDownloadButtonRef}
            apiMethod="post"
            api="/api/GenerateCI"
            apiData={{
              Business_Unit_Id: selectedBusinessUnit,
              Deal_PFI_Id: selectedPFI,
              Tranche_Id: selectedTranche?.id,
              Revision_Number: selectedDocumentRevisionNumber?.value === "Default" ? "Default" : isNaN(selectedDocumentRevisionNumber?.value?.slice(1)) ? null : parseInt(selectedDocumentRevisionNumber?.value?.slice(1), 10),
              User_Name: currentUser?.name,
              User_Id: currentUser?.id,
              is_approved_document: selectedTab === 1,
              is_foods_and_polymers_deal: isFoodsOrPolymersDeal,
              is_sblc_deal: isSBLCDeal,
              commercial_invoice_vantira: (Boolean(mainEntityType === "vantira") && isLinkedThroughRoute) ? commmercialInvoiceNo : formData?.Commercial_Invoice_No,
              is_vantira_deal: Boolean(mainEntityType === "vantira") || false,
              vantira_flag: isLinkedThroughRoute ? Boolean(mainEntityType === "vantira") : false,
              linked_tranche_id: linkedTranche?.id
            }}
            buttonText="Generate CI"
            fileName="CI"
            isDisabled={!selectedTranche}
            toggleDocumentList={toggleDocumentList}
            setToggleDocumentList={setToggleDocumentList}
            setToggleFileList={setToggleFileList}
            handleResetModal={handleCloseModal}
            setIsLoading={setIsCIDocumentGenerating}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setAlertType={setAlertType}

            isLinkedDocument={isLinkedThroughRoute && linkedPFI?.id}
            entityType={mainEntityType}
            onMissingFieldsDetected={handleMissingFieldsDetected}
          />

          {/* Linked CI Document Downloader */}
          {isLinkedThroughRoute && linkedTranche?.id && linkedPFI?.id && (
            <ReportDownloader
              ref={linkedCiDocumentDownloadButtonRef}
              apiMethod="post"
              api="/api/GenerateCI"
              apiData={{
                Business_Unit_Id: selectedBusinessUnit,
                Deal_PFI_Id: linkedPFI?.id,
                Tranche_Id: linkedTranche?.id,
                Revision_Number: selectedDocumentRevisionNumber?.value === "Default" ? "Default" : isNaN(selectedDocumentRevisionNumber?.value?.slice(1)) ? null : parseInt(selectedDocumentRevisionNumber?.value?.slice(1), 10),
                User_Name: currentUser?.name,
                User_Id: currentUser?.id,
                is_approved_document: selectedTab === 1,
                is_foods_and_polymers_deal: isFoodsOrPolymersDeal,
                is_sblc_deal: isSBLCDeal,
                commercial_invoice_vantira: Boolean(linkedEntityType === "vantira") ? commmercialInvoiceNo : null,
                is_vantira_deal: Boolean(linkedEntityType === "vantira") || false,
                linked_tranche_id: selectedTranche?.id
              }}
              buttonText="Generate Linked CI"
              fileName="CI_Linked"
              isDisabled={!linkedTranche}
              toggleDocumentList={toggleDocumentList}
              setToggleDocumentList={setToggleDocumentList}
              setToggleFileList={setToggleFileList}
              handleResetModal={handleCloseModal}
              setIsLoading={setIsCIDocumentGenerating}
              setShowAlert={setShowAlert}
              setAlertMessage={setAlertMessage}
              setAlertType={setAlertType}

              isLinkedDocument={true}
              entityType={linkedEntityType}
              onMissingFieldsDetected={handleMissingFieldsDetected}
            />
          )}

          <ReportDownloader
            ref={saDocumentDownloadButtonRef}
            apiMethod="post"
            api="/api/GenerateSA"
            apiData={{
              Business_Unit_Id: selectedBusinessUnit,
              Deal_PFI_Id: selectedPFI,
              Tranche_Id: selectedTranche?.id,
              Revision_Number: selectedDocumentRevisionNumber?.value === "Default" ? "Default" : isNaN(selectedDocumentRevisionNumber?.value?.slice(1)) ? null : parseInt(selectedDocumentRevisionNumber?.value?.slice(1), 10),
              User_Name: currentUser?.name,
              User_Id: currentUser?.id,
              is_approved_document: selectedTab === 1,
              is_foods_and_polymers_deal: isFoodsOrPolymersDeal,
              is_sblc_deal: isSBLCDeal,
              commercial_invoice_vantira: Boolean(mainEntityType === "vantira") ? commmercialInvoiceNo : null,
              is_vantira_deal: Boolean(mainEntityType === "vantira") || false,
              linked_tranche_id: linkedTranche?.id
            }}
            buttonText="Generate SA"
            fileName="SA"
            isDisabled={!selectedTranche}
            toggleDocumentList={toggleDocumentList}
            setToggleDocumentList={setToggleDocumentList}
            setToggleFileList={setToggleFileList}
            handleResetModal={handleCloseModal}
            setIsLoading={setIsSADocumentGenerating}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setAlertType={setAlertType}

            isLinkedDocument={isLinkedThroughRoute && linkedPFI?.id}
            entityType={mainEntityType}
            onMissingFieldsDetected={handleMissingFieldsDetected}
          />

          {/* Linked SA Document Downloader */}
          {isLinkedThroughRoute && linkedTranche?.id && linkedPFI?.id && (
            <ReportDownloader
              ref={linkedSaDocumentDownloadButtonRef}
              apiMethod="post"
              api="/api/GenerateSA"
              apiData={{
                Business_Unit_Id: selectedBusinessUnit,
                Deal_PFI_Id: linkedPFI?.id,
                Tranche_Id: linkedTranche?.id,
                Revision_Number: selectedDocumentRevisionNumber?.value === "Default" ? "Default" : isNaN(selectedDocumentRevisionNumber?.value?.slice(1)) ? null : parseInt(selectedDocumentRevisionNumber?.value?.slice(1), 10),
                User_Name: currentUser?.name,
                User_Id: currentUser?.id,
                is_approved_document: selectedTab === 1,
                is_foods_and_polymers_deal: isFoodsOrPolymersDeal,
                is_sblc_deal: isSBLCDeal,
                commercial_invoice_vantira: Boolean(linkedEntityType === "vantira") ? commmercialInvoiceNo : null,
                is_vantira_deal: Boolean(linkedEntityType === "vantira") || false,
                linked_tranche_id: selectedTranche?.id
              }}
              buttonText="Generate Linked SA"
              fileName="SA_Linked"
              isDisabled={!linkedTranche}
              toggleDocumentList={toggleDocumentList}
              setToggleDocumentList={setToggleDocumentList}
              setToggleFileList={setToggleFileList}
              handleResetModal={handleCloseModal}
              setIsLoading={setIsSADocumentGenerating}
              setShowAlert={setShowAlert}
              setAlertMessage={setAlertMessage}
              setAlertType={setAlertType}

              isLinkedDocument={true}
              entityType={linkedEntityType}
              onMissingFieldsDetected={handleMissingFieldsDetected}
            />
          )}

          <ReportDownloader
            ref={plDocumentDownloadButtonRef}
            apiMethod="post"
            api="/api/GeneratePL"
            apiData={{
              Business_Unit_Id: selectedBusinessUnit,
              Deal_PFI_Id: selectedPFI,
              Tranche_Id: selectedTranche?.id,
              Revision_Number: selectedDocumentRevisionNumber?.value === "Default" ? "Default" : isNaN(selectedDocumentRevisionNumber?.value?.slice(1)) ? null : parseInt(selectedDocumentRevisionNumber?.value?.slice(1), 10),
              User_Name: currentUser?.name,
              User_Id: currentUser?.id,
              is_approved_document: selectedTab === 1,
              is_foods_and_polymers_deal: isFoodsOrPolymersDeal,
              is_sblc_deal: isSBLCDeal,
              commercial_invoice_vantira: Boolean(mainEntityType === "vantira") ? commmercialInvoiceNo : null,
              is_vantira_deal: Boolean(mainEntityType === "vantira") || false,
              linked_tranche_id: linkedTranche?.id
            }}
            buttonText="Generate PL"
            fileName="PL"
            isDisabled={!selectedTranche}
            toggleDocumentList={toggleDocumentList}
            setToggleDocumentList={setToggleDocumentList}
            setToggleFileList={setToggleFileList}
            handleResetModal={handleCloseModal}
            setIsLoading={setIsPLDocumentGenerating}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setAlertType={setAlertType}

            isLinkedDocument={isLinkedThroughRoute && linkedPFI?.id}
            entityType={mainEntityType}
            onMissingFieldsDetected={handleMissingFieldsDetected}
          />

          {/* Linked PL Document Downloader */}
          {isLinkedThroughRoute && linkedTranche?.id && linkedPFI?.id && (
            <ReportDownloader
              ref={linkedPlDocumentDownloadButtonRef}
              apiMethod="post"
              api="/api/GeneratePL"
              apiData={{
                Business_Unit_Id: selectedBusinessUnit,
                Deal_PFI_Id: linkedPFI?.id,
                Tranche_Id: linkedTranche?.id,
                Revision_Number: selectedDocumentRevisionNumber?.value === "Default" ? "Default" : isNaN(selectedDocumentRevisionNumber?.value?.slice(1)) ? null : parseInt(selectedDocumentRevisionNumber?.value?.slice(1), 10),
                User_Name: currentUser?.name,
                User_Id: currentUser?.id,
                is_approved_document: selectedTab === 1,
                is_foods_and_polymers_deal: isFoodsOrPolymersDeal,
                is_sblc_deal: isSBLCDeal,
                commercial_invoice_vantira: Boolean(linkedEntityType === "vantira") ? commmercialInvoiceNo : null,
                is_vantira_deal: Boolean(linkedEntityType === "vantira") || false,
                linked_tranche_id: selectedTranche?.id
              }}
              buttonText="Generate Linked PL"
              fileName="PL_Linked"
              isDisabled={!linkedTranche}
              toggleDocumentList={toggleDocumentList}
              setToggleDocumentList={setToggleDocumentList}
              setToggleFileList={setToggleFileList}
              handleResetModal={handleCloseModal}
              setIsLoading={setIsPLDocumentGenerating}
              setShowAlert={setShowAlert}
              setAlertMessage={setAlertMessage}
              setAlertType={setAlertType}

              isLinkedDocument={true}
              entityType={linkedEntityType}
              onMissingFieldsDetected={handleMissingFieldsDetected}
            />
          )}

          <ReportDownloader
            ref={beDocumentDownloadButtonRef}
            apiMethod="post"
            api="/api/GenerateBE"
            apiData={{
              Business_Unit_Id: selectedBusinessUnit,
              Deal_PFI_Id: selectedPFI,
              Tranche_Id: selectedTranche?.id,
              Revision_Number: selectedDocumentRevisionNumber?.value === "Default" ? "Default" : isNaN(selectedDocumentRevisionNumber?.value?.slice(1)) ? null : parseInt(selectedDocumentRevisionNumber?.value?.slice(1), 10),
              User_Name: currentUser?.name,
              User_Id: currentUser?.id,
              is_approved_document: selectedTab === 1,
              is_foods_and_polymers_deal: isFoodsOrPolymersDeal,
              is_sblc_deal: isSBLCDeal,
              commercial_invoice_vantira: Boolean(mainEntityType === "vantira") ? commmercialInvoiceNo : null,
              is_vantira_deal: Boolean(mainEntityType === "vantira") || false,
              linked_tranche_id: linkedTranche?.id
            }}
            buttonText="Generate BE"
            fileName="BE"
            isDisabled={!selectedTranche}
            toggleDocumentList={toggleDocumentList}
            setToggleDocumentList={setToggleDocumentList}
            setToggleFileList={setToggleFileList}
            handleResetModal={handleCloseModal}
            setIsLoading={setIsBEDocumentGenerating}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setAlertType={setAlertType}

            isLinkedDocument={isLinkedThroughRoute && linkedPFI?.id}
            entityType={mainEntityType}
            onMissingFieldsDetected={handleMissingFieldsDetected}
          />

          {/* Linked BE Document Downloader */}
          {isLinkedThroughRoute && linkedTranche?.id && linkedPFI?.id && (
            <ReportDownloader
              ref={linkedBeDocumentDownloadButtonRef}
              apiMethod="post"
              api="/api/GenerateBE"
              apiData={{
                Business_Unit_Id: selectedBusinessUnit,
                Deal_PFI_Id: linkedPFI?.id,
                Tranche_Id: linkedTranche?.id,
                Revision_Number: selectedDocumentRevisionNumber?.value === "Default" ? "Default" : isNaN(selectedDocumentRevisionNumber?.value?.slice(1)) ? null : parseInt(selectedDocumentRevisionNumber?.value?.slice(1), 10),
                User_Name: currentUser?.name,
                User_Id: currentUser?.id,
                is_approved_document: selectedTab === 1,
                is_foods_and_polymers_deal: isFoodsOrPolymersDeal,
                is_sblc_deal: isSBLCDeal,
                commercial_invoice_vantira: Boolean(linkedEntityType === "vantira") ? commmercialInvoiceNo : null,
                is_vantira_deal: Boolean(linkedEntityType === "vantira") || false,
                linked_tranche_id: selectedTranche?.id
              }}
              buttonText="Generate Linked BE"
              fileName="BE_Linked"
              isDisabled={!linkedTranche}
              toggleDocumentList={toggleDocumentList}
              setToggleDocumentList={setToggleDocumentList}
              setToggleFileList={setToggleFileList}
              handleResetModal={handleCloseModal}
              setIsLoading={setIsBEDocumentGenerating}
              setShowAlert={setShowAlert}
              setAlertMessage={setAlertMessage}
              setAlertType={setAlertType}

              isLinkedDocument={true}
              entityType={linkedEntityType}
              onMissingFieldsDetected={handleMissingFieldsDetected}
            />
          )}

          <ReportDownloader
            ref={cooDocumentDownloadButtonRef}
            apiMethod="post"
            api="/api/GenerateOtherCertificates"
            apiData={{
              Business_Unit_Id: selectedBusinessUnit,
              Deal_PFI_Id: selectedPFI,
              Tranche_Id: selectedTranche?.id,
              User_Id: currentUser?.id,
              COO_ID: selectedCOO?.id,
              User_Name: currentUser?.name,
              is_approved_document: selectedTab === 1,
              is_foods_and_polymers_deal: isFoodsOrPolymersDeal,
              is_sblc_deal: isSBLCDeal,
              commercial_invoice_vantira: Boolean(mainEntityType === "vantira") ? commmercialInvoiceNo : null,
              is_vantira_deal: Boolean(mainEntityType === "vantira") || false,
              linked_tranche_id: linkedTranche?.id
            }}
            buttonText="Generate Other Certificate"
            fileName="COO"
            isDisabled={!selectedTranche}
            toggleDocumentList={toggleDocumentList}
            setToggleDocumentList={setToggleDocumentList}
            setToggleFileList={setToggleFileList}
            handleResetModal={handleCloseModal}
            setIsLoading={setIsCOODocumentGenerating}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setAlertType={setAlertType}

            isLinkedDocument={isLinkedThroughRoute && linkedPFI?.id}
            entityType={mainEntityType}
            onMissingFieldsDetected={handleMissingFieldsDetected}
          />

          {/* Linked COO Document Downloader */}
          {isLinkedThroughRoute && linkedTranche?.id && linkedPFI?.id && (
            <ReportDownloader
              ref={linkedCooDocumentDownloadButtonRef}
              apiMethod="post"
              api="/api/GenerateOtherCertificates"
              apiData={{
                Business_Unit_Id: selectedBusinessUnit,
                Deal_PFI_Id: linkedPFI?.id,
                Tranche_Id: linkedTranche?.id,
                User_Id: currentUser?.id,
                COO_ID: selectedLinkedCOO?.id, // ← Using selectedLinkedCOO
                User_Name: currentUser?.name,
                is_approved_document: selectedTab === 1,
                is_foods_and_polymers_deal: isFoodsOrPolymersDeal,
                is_sblc_deal: isSBLCDeal,
                commercial_invoice_vantira: Boolean(linkedEntityType === "vantira") ? commmercialInvoiceNo : null,
                is_vantira_deal: Boolean(linkedEntityType === "vantira") || false,
                linked_tranche_id: selectedTranche?.id
              }}
              buttonText="Generate Linked Other Certificate"
              fileName="COO_Linked"
              isDisabled={!linkedTranche}
              toggleDocumentList={toggleDocumentList}
              setToggleDocumentList={setToggleDocumentList}
              setToggleFileList={setToggleFileList}
              handleResetModal={handleCloseModal}
              setIsLoading={setIsCOODocumentGenerating}
              setShowAlert={setShowAlert}
              setAlertMessage={setAlertMessage}
              setAlertType={setAlertType}
              isLinkedDocument={true}
              entityType={linkedEntityType}
              onMissingFieldsDetected={handleMissingFieldsDetected}
            />
          )}


        </>
      )
    },
    {
      id: 10,
      columns: 6,
      title: "Document Status",
      content: (
        <>
          <Grid
            container
            spacing={"10px"}
            className="align-items-start autocomplete mb-3"
          >
            <Grid size={{ xs: 6 }}>
              <VirtualizedAutocomplete
                isMultiple={false}
                isObject={false}
                isWritable={isWritable}
                isLocked={Boolean(selectedTranche?.period_lock && isFieldLocked("Supplier_Status", "Documentation", "Post-Shipment", lockedFields))}
                filterOn="Supplier Status"
                options={buyerDocumentStatus}
                selectedOptions={formData?.Supplier_Status || null}
                handleSelectChange={(filterOn, newValue) => handleChange(null, "Supplier_Status", newValue)}
              />
            </Grid>

            {formData?.Supplier_Status?.includes("Discrepant") && (
              <>
                <Grid size={{ xs: 6 }}>
                  <VirtualizedAutocomplete
                    isMultiple={false}
                    isObject={false}
                    isWritable={isWritable}
                    isLocked={Boolean(selectedTranche?.period_lock && isFieldLocked("Supplier_Discrepancy_Acceptance_Swift", "Documentation", "Post-Shipment", lockedFields))}
                    filterOn="Supplier Discrepancy Acceptance Swift"
                    options={discrepancyAcceptanceSwifts}
                    selectedOptions={formData?.Supplier_Discrepancy_Acceptance_Swift || null}
                    handleSelectChange={(filterOn, newValue) => handleChange(null, "Supplier_Discrepancy_Acceptance_Swift", newValue)}
                  />
                </Grid>

                <Grid size={{ xs: 12 }} className="form-text-field">
                  <TextField
                    label="Supplier Discrepancy Reason"
                    id="Supplier_Discrepancy_Reason"
                    disabled={Boolean(selectedTranche?.period_lock && isFieldLocked("Supplier_Discrepancy_Reason", "Documentation", "Post-Shipment", lockedFields))}
                    multiline
                    rows={3}
                    value={formData?.Supplier_Discrepancy_Reason || ""}
                    onChange={(event) => handleChange(event, "Supplier_Discrepancy_Reason", event.target.value)}
                    slotProps={{
                      input: {
                        readOnly: !isWritable
                      }
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Grid
            container
            spacing={"10px"}
            className="align-items-start autocomplete"
          >
            <Grid size={{ xs: 6 }}>
              <VirtualizedAutocomplete
                isMultiple={false}
                isObject={false}
                isWritable={isWritable}
                isLocked={Boolean(selectedTranche?.period_lock && isFieldLocked("Customer_Status", "Documentation", "Post-Shipment", lockedFields))}
                filterOn="Customer Status"
                options={sellerDocumentStatus}
                selectedOptions={formData?.Customer_Status || null}
                handleSelectChange={(filterOn, newValue) => handleChange(null, "Customer_Status", newValue)}
              />
            </Grid>

            {formData?.Customer_Status?.includes("Discrepant") && (
              <>
                <Grid size={{ xs: 6 }}>
                  <VirtualizedAutocomplete
                    isMultiple={false}
                    isObject={false}
                    isWritable={isWritable}
                    isLocked={Boolean(selectedTranche?.period_lock && isFieldLocked("Customer_Discrepancy_Acceptance_Swift", "Documentation", "Post-Shipment", lockedFields))}
                    filterOn="Customer Discrepancy Acceptance Swift"
                    options={discrepancyAcceptanceSwifts}
                    selectedOptions={formData?.Customer_Discrepancy_Acceptance_Swift || null}
                    handleSelectChange={(filterOn, newValue) => handleChange(null, "Customer_Discrepancy_Acceptance_Swift", newValue)}
                  />
                </Grid>

                <Grid size={{ xs: 12 }} className="form-text-field">
                  <TextField
                    label="Customer Discrepancy Reason"
                    id="Customer_Discrepancy_Reason"
                    disabled={Boolean(selectedTranche?.period_lock && isFieldLocked("Customer_Discrepancy_Reason", "Documentation", "Post-Shipment", lockedFields))}
                    multiline
                    rows={3}
                    value={formData?.Customer_Discrepancy_Reason || ""}
                    onChange={(event) => handleChange(event, "Customer_Discrepancy_Reason", event.target.value)}
                    slotProps={{
                      input: {
                        readOnly: !isWritable
                      }
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </>
      )
    },
    {
      id: 11,
      columns: 12,
      title: "Document Library",
      content: (
        <FileUploader
          isWritable={(isWritable ? isDocumentWritable : isExistingDocumentation ? isDocumentWritable : false)}
          isLocked={selectedTranche?.period_lock}
          isLoading={areFilesLoading}
          pathname={pathname}
          files={files}
          uploadedFiles={uploadedFiles}
          allowFileTypeSelection={true}
          buyingLegFileTypes={buyingLegFileTypes}
          sellingLegFileTypes={sellingLegFileTypes}
          setIsLoading={setIsLoading}
          setShowAlert={setShowAlert}
          setAlertMessage={setAlertMessage}
          setAlertType={setAlertType}
          setFiles={setFiles}
          setUploadedFiles={setUploadedFiles}
          logOut={logOut}
        />
      )
    },
    // {
    //   id: 9,
    //   columns: 12,
    //   title: "Document Request",
    //   content: (
    //     <>
    //       <Grid
    //           container
    //           spacing = {2}
    //           className = "align-items-start justify-content-center document-request-buttons"
    //         >
    //           <Grid size = {{ xs: 4 }}>
    //             <DocumentRequester
    //               requestType = "PFI"
    //               fileType = "Deal Sheet"
    //               isWritable = {documentRequestPermissions["PFI"]?.isWritable && selectedTranche?.id}
    //               isDocumentWritable = {true}
    //               isLocked = {false}
    //               pathname = {pathname}
    //               selectedPFI = {selectedPFI}
    //               selectedTranche = {selectedTranche}
    //               selectedBusinessUnit = {selectedBusinessUnit}
    //               setIsLoading = {setIsLoading}
    //               uploadedFiles = {requestedFiles} 
    //               buyingLegFileTypes = {buyingLegFileTypes}
    //               sellingLegFileTypes = {sellingLegFileTypes}
    //               setShowAlert = {setShowAlert}
    //               setAlertMessage = {setAlertMessage}
    //               setAlertType = {setAlertType}
    //               files = {files}
    //               allowFileTypeSelection = {true}
    //               setFiles = {setFiles}
    //               setUploadedFiles = {setRequestedFiles}
    //               triggerSessionExpire = {triggerSessionExpire}
    //               logOut = {logOut}
    //             />
    //           </Grid>

    //           <Grid size = {{ xs: 4 }}>
    //             <DocumentRequester
    //               requestType = "SI"
    //               fileType = "Document"
    //               isWritable = {documentRequestPermissions["SI"]?.isWritable && selectedTranche?.id}
    //               isDocumentWritable = {true}
    //               isLocked = {false}
    //               pathname = {pathname}
    //               selectedPFI = {selectedPFI}
    //               selectedTranche = {selectedTranche}
    //               selectedBusinessUnit = {selectedBusinessUnit}
    //               uploadedFiles = {requestedFiles}
    //               buyingLegFileTypes = {buyingLegFileTypes}
    //               sellingLegFileTypes = {sellingLegFileTypes}
    //               setIsLoading = {setIsLoading}
    //               setShowAlert = {setShowAlert}
    //               setAlertMessage = {setAlertMessage}
    //               setAlertType = {setAlertType}
    //               allowFileTypeSelection = {true}
    //               setFiles = {setFiles}
    //               setUploadedFiles = {setRequestedFiles}
    //               triggerSessionExpire = {triggerSessionExpire}
    //               logOut = {logOut}
    //             />
    //           </Grid>

    //           <Grid size = {{ xs: 4 }}>
    //             <DocumentRequester
    //               requestType = "SI Approval"
    //               fileType = "Document"
    //               isWritable = {documentRequestPermissions["SI Approval"]?.isWritable && selectedTranche?.id}
    //               isDocumentWritable = {true}
    //               isLocked = {false}
    //               pathname = {pathname}
    //               selectedPFI = {selectedPFI}
    //               selectedTranche = {selectedTranche}
    //               selectedBusinessUnit = {selectedBusinessUnit}
    //               uploadedFiles = {requestedFiles}
    //               buyingLegFileTypes = {buyingLegFileTypes}
    //               sellingLegFileTypes = {sellingLegFileTypes}
    //               setIsLoading = {setIsLoading}
    //               setShowAlert = {setShowAlert}
    //               setAlertMessage = {setAlertMessage}
    //               setAlertType = {setAlertType}
    //               allowFileTypeSelection = {true}
    //               setFiles = {setFiles}
    //               setUploadedFiles = {setRequestedFiles}
    //               triggerSessionExpire = {triggerSessionExpire}
    //               logOut = {logOut}
    //             />
    //           </Grid>

    //           <Grid size = {{ xs: 4 }}>
    //             <DocumentRequester
    //               requestType = "Post-Shipment Documents"
    //               fileType = "Document"
    //               isWritable = {documentRequestPermissions["Post-Shipment Documents"]?.isWritable && selectedTranche?.id}
    //               isDocumentWritable = {true}
    //               isLocked = {false}
    //               pathname = {pathname}
    //               selectedPFI = {selectedPFI}
    //               selectedTranche = {selectedTranche}
    //               selectedBusinessUnit = {selectedBusinessUnit}
    //               uploadedFiles = {requestedFiles}
    //               buyingLegFileTypes = {buyingLegFileTypes}
    //               sellingLegFileTypes = {sellingLegFileTypes}
    //               setIsLoading = {setIsLoading}
    //               setShowAlert = {setShowAlert}
    //               setAlertMessage = {setAlertMessage}
    //               setAlertType = {setAlertType}
    //               allowFileTypeSelection = {true}
    //               setFiles = {setFiles}
    //               setUploadedFiles = {setRequestedFiles}
    //               triggerSessionExpire = {triggerSessionExpire}
    //               logOut = {logOut}
    //             />
    //           </Grid>

    //           <Grid size = {{ xs: 4 }}>
    //             <DocumentRequester
    //               requestType = "Documentation Approval"
    //               fileType = "Document"
    //               isWritable = {documentRequestPermissions["Documentation Approval"]?.isWritable && selectedTranche?.id}
    //               isDocumentWritable = {true}
    //               isLocked = {false}
    //               pathname = {pathname}
    //               selectedPFI = {selectedPFI}
    //               selectedTranche = {selectedTranche}
    //               selectedBusinessUnit = {selectedBusinessUnit}
    //               uploadedFiles = {requestedFiles}
    //               buyingLegFileTypes = {buyingLegFileTypes}
    //               sellingLegFileTypes = {sellingLegFileTypes}
    //               setIsLoading = {setIsLoading}
    //               setShowAlert = {setShowAlert}
    //               setAlertMessage = {setAlertMessage}
    //               setAlertType = {setAlertType}
    //               allowFileTypeSelection = {true}
    //               setFiles = {setFiles}
    //               setUploadedFiles = {setRequestedFiles}
    //               triggerSessionExpire = {triggerSessionExpire}
    //               logOut = {logOut}
    //             />
    //           </Grid>

    //           <Grid size = {{ xs: 4 }}>
    //             <DocumentRequester
    //               requestType = "LC Draft Review"
    //               fileType = "LC Draft"
    //               isWritable = {documentRequestPermissions["LC Draft Review"]?.isWritable && selectedTranche?.id}
    //               isDocumentWritable = {true}
    //               isLocked = {false}
    //               pathname = {pathname}
    //               selectedPFI = {selectedPFI}
    //               selectedTranche = {selectedTranche}
    //               selectedBusinessUnit = {selectedBusinessUnit}
    //               uploadedFiles = {requestedFiles}
    //               buyingLegFileTypes = {buyingLegFileTypes}
    //               sellingLegFileTypes = {sellingLegFileTypes}
    //               setIsLoading = {setIsLoading}
    //               setShowAlert = {setShowAlert}
    //               setAlertMessage = {setAlertMessage}
    //               setAlertType = {setAlertType}
    //               allowFileTypeSelection = {true}
    //               setFiles = {setFiles}
    //               setUploadedFiles = {setRequestedFiles}
    //               triggerSessionExpire = {triggerSessionExpire}
    //               logOut = {logOut}
    //             />
    //           </Grid>

    //           <Grid size = {{ xs: 4 }}>
    //             <DocumentRequester
    //               requestType = "Post Transmission Review of LC"
    //               fileType = "Transmitted ELC"
    //               isWritable = {documentRequestPermissions["Post Transmission Review of LC"]?.isWritable && selectedTranche?.id}
    //               isDocumentWritable = {true}
    //               isLocked = {false}
    //               pathname = {pathname}
    //               selectedPFI = {selectedPFI}
    //               selectedTranche = {selectedTranche}
    //               selectedBusinessUnit = {selectedBusinessUnit}
    //               uploadedFiles = {requestedFiles}
    //               buyingLegFileTypes = {buyingLegFileTypes}
    //               sellingLegFileTypes = {sellingLegFileTypes}
    //               setIsLoading = {setIsLoading}
    //               setShowAlert = {setShowAlert}
    //               setAlertMessage = {setAlertMessage}
    //               setAlertType = {setAlertType}
    //               allowFileTypeSelection = {true}
    //               setFiles = {setFiles}
    //               setUploadedFiles = {setRequestedFiles}
    //               triggerSessionExpire = {triggerSessionExpire}
    //               logOut = {logOut}
    //             />
    //           </Grid>

    //           <Grid size = {{ xs: 4 }}>
    //             <DocumentRequester
    //               requestType = "LC Ammendments Review"
    //               fileType = "LC Draft"
    //               isWritable = {documentRequestPermissions["LC Ammendments Review"]?.isWritable && selectedTranche?.id}
    //               isDocumentWritable = {true}
    //               isLocked = {false}
    //               pathname = {pathname}
    //               selectedPFI = {selectedPFI}
    //               selectedTranche = {selectedTranche}
    //               selectedBusinessUnit = {selectedBusinessUnit}
    //               uploadedFiles = {requestedFiles}
    //               buyingLegFileTypes = {buyingLegFileTypes}
    //               sellingLegFileTypes = {sellingLegFileTypes}
    //               setIsLoading = {setIsLoading}
    //               setShowAlert = {setShowAlert}
    //               setAlertMessage = {setAlertMessage}
    //               setAlertType = {setAlertType}
    //               allowFileTypeSelection = {true}
    //               setFiles = {setFiles}
    //               setUploadedFiles = {setRequestedFiles}
    //               triggerSessionExpire = {triggerSessionExpire}
    //               logOut = {logOut}
    //             />
    //           </Grid>

    //           <Grid size = {{ xs: 4 }}>
    //             <DocumentRequester
    //               requestType = "BL Draft Review"
    //               fileType = "BL Draft"
    //               isWritable = {documentRequestPermissions["BL Draft Review"]?.isWritable && selectedTranche?.id}
    //               isDocumentWritable = {true}
    //               isLocked = {false}
    //               pathname = {pathname}
    //               selectedPFI = {selectedPFI}
    //               selectedTranche = {selectedTranche}
    //               selectedBusinessUnit = {selectedBusinessUnit}
    //               uploadedFiles = {requestedFiles}
    //               buyingLegFileTypes = {buyingLegFileTypes}
    //               sellingLegFileTypes = {sellingLegFileTypes}
    //               setIsLoading = {setIsLoading}
    //               setShowAlert = {setShowAlert}
    //               setAlertMessage = {setAlertMessage}
    //               setAlertType = {setAlertType}
    //               allowFileTypeSelection = {true}
    //               setFiles = {setFiles}
    //               setUploadedFiles = {setRequestedFiles}
    //               triggerSessionExpire = {triggerSessionExpire}
    //               logOut = {logOut}
    //             />
    //           </Grid>

    //       </Grid>
    //     </>
    //   )
    // },
  ];

  return (
    <>
      <form ref={formRef} className="deal-form multi-form-container">
        <div className="d-flex flex-wrap gap-2">
          {formSectionsHTML.map((section, index) => (
            <Grid
              key={index}
              size={{ xs: section?.columns }}
              className="form-text-field"
            >
              <Accordion
                key={section.id}
                expanded={expandedFormSections.includes(section.id)}
                onChange={handleSectionAccordionChange(section.id)}
              >
                <AccordionSummary
                  id="panel-header"
                  aria-controls="panel-content"
                  expandIcon={<ExpandMoreIcon />}
                  slot="div"
                >
                  <small className="page-heading title">
                    {section.title}
                  </small>

                  {section.title === "Target Dates" && (
                    <Tooltip arrow title="The target dates will automatically populate upon saving the form.">
                      <IconButton size="small" onClick={(event) => event.stopPropagation()}>
                        <InfoIcon sx={{ color: '#aab2b6' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </AccordionSummary>
                <AccordionDetails>
                  {section.content}
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))}
        </div>
      </form>

      <Modal show={showDocumentModal} onHide={handleCloseModal} centered>
        <Modal.Header style={{ padding: '7px 16px' }} closeButton>
          <Modal.Title>{documentType === "COO" ? "Certificate" : documentType} Document</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0 px-0">
          <Tabs
            centered
            value={selectedTab}
            onChange={(event, newValue) => handleTabChange(newValue)}
            sx={{
              minHeight: '38px !important',
              marginBottom: '1em',
              backgroundColor: '#eef2ff',
              '& .MuiTab-root': {
                minHeight: '38px !important'
              }
            }}
          >
            <Tab label="Draft Documents" />
            <Tab label="Approved Documents" />
          </Tabs>

          <div className="px-3">
            <span>
              {selectedTab === 0
                ? documentType === "COO"
                  ? 'Select a draft document to generate or to view.'
                  : "Select a previous document to apply changes, view a document, or click 'Generate Draft Document' to create a new one."
                : 'Select an approved document to view.'
              }
            </span>

            {/* ADD THIS SECTION FOR CI */}
            {documentType === "CI" && selectedTab === 0 && isLinkedThroughRoute && (
              <div className="mt-3 mb-2">
                <TextField
                  label="Commercial Invoice No. VTE"
                  id="Commercial_Invoice_No_Modal"
                  required
                  fullWidth
                  value={commmercialInvoiceNo || ""}
                  onChange={(event) => setCommmercialInvoiceNo(event.target.value)}
                  disabled={!isWritable || !isLinkedThroughRoute}
                  size="small"
                  slotProps={{
                    input: {
                      readOnly: !isWritable || !isLinkedThroughRoute,
                    }
                  }}
                  helperText="Required to generate CI document"
                />
              </div>
            )}

            <div className="mt-2">
              <VirtualizedAutocomplete
                isLoading={areDocumentsLoading || isLoadingLinkedCoos}
                isMultiple={false}
                isObject={true}
                isWritable={true}
                filterOn={documentType === "COO" ? "Select Certificate" : "Select Document Revision Number"}
                options={selectedTab === 0 ? draftDocuments : approvedDocuments}
                selectedOptions={documentType === "COO" ? selectedCOO : selectedDocumentRevisionNumber || null}
                handleSelectChange={(filterOn, newValue) => {
                  if (documentType === "COO") {
                    setSelectedCOO(newValue);

                    if (isLinkedThroughRoute && linkedTrancheCoos.length > 0 && newValue) {
                      const selectedIndex = coos.findIndex(coo => coo.value === newValue.value);
                      if (selectedIndex !== -1 && linkedTrancheCoos[selectedIndex]) {
                        setSelectedLinkedCOO(linkedTrancheCoos[selectedIndex]);
                      } else {
                        setSelectedLinkedCOO(null);
                      }
                    }
                  } else {
                    setSelectedDocumentRevisionNumber(newValue);
                  }
                }}
              />
            </div>

            {(documentType === "PFI" && selectedTab === 0) && (
              <div className="d-flex align-items-center gap-1">
                <Checkbox
                  size="small"
                  className="p-0"
                  disabled={!isWritable}
                  onChange={(event) => setAddTermsAndConditionsPage(event.target.checked)}
                />
                <Typography fontSize="small">Include <b>Terms & Conditions</b> with the document?</Typography>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="gap-2">
          <Button variant="outlined" size="small" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={documentType === "COO" ? (!selectedCOO || (selectedTab === 0 && !selectedCOO.isGenerated)) : !selectedDocumentRevisionNumber}
            onClick={handleViewDocument}
          >
            View Document
          </Button>
          {selectedTab === 0 && (
            <Tooltip
              title={
                documentType === "CI" && !commmercialInvoiceNo && isCommercialInvoiceEditable
                  ? "Please fill in Commercial Invoice No. above to generate CI document"
                  : ""
              }
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  size="small"
                  disabled={
                    !isWritable ||
                    (selectedTab === 1 && documentType !== "COO" && !selectedDocumentRevisionNumber) ||
                    (documentType === "BE" && !documentGenerationData?.["CI"]?.is_document_generated) ||
                    (documentType === "COO" && !selectedCOO) ||
                    (documentType === "CI" && isCommercialInvoiceEditable && !commmercialInvoiceNo)
                  }
                  onClick={handleGenerateDocument}
                >
                  Generate Draft Document
                </Button>
              </span>
            </Tooltip>
          )}
        </Modal.Footer>
      </Modal>
      <CombinedMissingFieldsModal
        show={showMissingFieldsModal}
        onHide={() => {
          setShowMissingFieldsModal(false);
          handleCloseCombinedModal();
        }}
        vantiraFields={combinedMissingFields.vantira}
        fzeFields={combinedMissingFields.fze}
      />
    </>
  );
});

export default memo(Documentation);